import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import './AddMaterial.css';
import { ArrowIcon } from '../../Shared/TrainingIcons';
// @ts-ignore
import { getUsersSummary } from '../../../../api/api';
import { UserContext } from '../../../../contexts/CurrentUserContext';
import {
  createKnowledgeEntry,
  updateKnowledgeEntry,
  createLesson,
  updateLesson,
  uploadImage,
  uploadLessonMaterial,
  uploadBlockMedia,
  createLessonTest,
  createTestQuestion,
  createAnswerOption,
  updateAnswerOption,
  getLessonTest,
  deleteImage,
  deleteLesson,
  deleteLessonTest,
  getLessons,
  getLessonsByKnowledgeId,
  getLessonById,
  getLessonByKnowledgeAndLessonId,
  type CreateKnowledgeEntryRequest,
  type UpdateKnowledgeEntryRequest,
  type CreateBlockRequest,
  type CreateTestRequest,
  type CreateTestQuestionRequest,
  type CreateAnswerOptionRequest,
  type ApiLesson,
} from '../../../../api/knowledgeApi';

// Компонент для отображения превью файла
const FilePreview: React.FC<{ file: File }> = ({ file }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isImage = file.type.startsWith('image/');

  useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [file, isImage]);

  if (!isImage) {
    return null;
  }

  return (
    <img
      src={previewUrl || ''}
      alt={file.name}
      style={{
        width: '60px',
        height: '60px',
        objectFit: 'cover',
        borderRadius: '6px',
        border: '1px solid #e0e0e0'
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

const AddMaterial: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState<'training' | 'knowledge'>('training');
  const [isPublishing, setIsPublishing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editCourseId, setEditCourseId] = useState<string | null>(null);
  const [editCourseData, setEditCourseData] = useState<ApiLesson | null>(null);
  const [loadingCourseData, setLoadingCourseData] = useState(false);
  
  // Training form states
  const [trainingType, setTrainingType] = useState<'organizational' | 'profile'>('organizational');
  const [materialType, setMaterialType] = useState<'course' | 'lesson' | 'test'>('course');
  const [courseName, setCourseName] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [courseGoal, setCourseGoal] = useState('');
  const [courseTags, setCourseTags] = useState<Array<{ value: string; label: string }>>([]); // Поле для тегов (массив для CreatableSelect)
  const [availableTags, setAvailableTags] = useState<Array<{ value: string; label: string }>>([]); // Существующие теги для выбора
  const [tagsLoading, setTagsLoading] = useState(false);
  const [accessLevel, setAccessLevel] = useState<Array<{ value: number | string; label: string }>>([]);
  const [users, setUsers] = useState<Array<{ value: number; label: string }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const ALL_USERS_VALUE = 'all';
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [lessonsCount, setLessonsCount] = useState(0);
  const [testsCount, setTestsCount] = useState(0);
  
  // Standalone lesson state (when materialType is 'lesson')
  const [selectedCourseForLesson, setSelectedCourseForLesson] = useState<{ value: number; label: string } | null>(null);
  const [courses, setCourses] = useState<Array<{ value: number; label: string }>>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [standaloneLesson, setStandaloneLesson] = useState<Lesson | null>(null);
  
  // Standalone test state (when materialType is 'test')
  const [selectedCourseForTest, setSelectedCourseForTest] = useState<{ value: number; label: string } | null>(null);
  const [selectedLessonForTest, setSelectedLessonForTest] = useState<{ value: number; label: string } | null>(null);
  const [lessonsForTest, setLessonsForTest] = useState<Array<{ value: number; label: string }>>([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [standaloneTest, setStandaloneTest] = useState<Test | null>(null);
  
  // Test form states
  interface AnswerOption {
    id: string;
    text: string;
    isCorrect: boolean;
  }

  interface TestQuestion {
    id: string;
    type: 'single' | 'multiple' | 'open';
    text: string;
    options: AnswerOption[];
  }

  interface Test {
    id: string;
    questions: TestQuestion[];
    timeToComplete: string;
  }
  const [tests, setTests] = useState<Test[]>([]);
  
  // Lesson form states
  interface LessonBlock {
    id: string;
    type?: 'video' | 'file' | 'text-media'; // Тип контента блока
    videoLink?: string;
    lessonFile?: File | null;
    lessonFileUrl?: string; // URL файла при редактировании
    mediaFile?: File | null;
    mediaFileUrl?: string; // URL медиа при редактировании
    text?: string;
  }

  interface Lesson {
    id: string;
    contentType: 'video' | 'file' | 'text-media';
    lessonName: string;
    difficulty: string;
    duration: string;
    downloadMaterial: File | null;
    downloadMaterialUrl?: string; // URL материала для скачивания при редактировании
    blocks: LessonBlock[];
    test?: Test | null;
  }
  const [lessons, setLessons] = useState<Lesson[]>([]);
  
  // Knowledge base form states
  const [knowledgeMaterialType, setKnowledgeMaterialType] = useState<'standard' | 'regulations' | 'lesson'>('standard');
  const [standardName, setStandardName] = useState('');
  const [section, setSection] = useState('');
  const [classification, setClassification] = useState('');
  const [designStage, setDesignStage] = useState('');
  const [version, setVersion] = useState('');
  const [materialFile, setMaterialFile] = useState<File | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCoverFile(file);
    }
  };

  const handleMaterialFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setMaterialFile(file);
    }
  };

  const handleAddLesson = () => {
    const newLesson: Lesson = {
      id: Date.now().toString(),
      contentType: 'video',
      lessonName: '',
      difficulty: '',
      duration: '',
      downloadMaterial: null,
      blocks: []
    };
    setLessons(prev => [...prev, newLesson]);
    setLessonsCount(prev => prev + 1);
  };

  const handleLessonChange = (lessonId: string, field: keyof Lesson, value: any) => {
    setLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
    ));
  };

  const handleLessonFileSelect = (lessonId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleLessonChange(lessonId, 'downloadMaterial', file);
      // Очищаем URL при выборе нового файла
      handleLessonChange(lessonId, 'downloadMaterialUrl', undefined);
    }
  };

  const handleAddBlock = (lessonId: string) => {
    const newBlock: LessonBlock = {
      id: Date.now().toString(),
      type: 'video' // Тип по умолчанию
    };
    setLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, blocks: [...lesson.blocks, newBlock] } : lesson
    ));
  };

  const handleBlockChange = (lessonId: string, blockId: string, field: keyof LessonBlock, value: any) => {
    setLessons(prev => prev.map(lesson => 
      lesson.id === lessonId 
        ? { 
            ...lesson, 
            blocks: lesson.blocks.map(block => 
              block.id === blockId ? { ...block, [field]: value } : block
            )
          }
        : lesson
    ));
  };

  const handleBlockFileSelect = (lessonId: string, blockId: string, field: 'lessonFile' | 'mediaFile', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleBlockChange(lessonId, blockId, field, file);
      // Очищаем URL при выборе нового файла
      if (field === 'lessonFile') {
        handleBlockChange(lessonId, blockId, 'lessonFileUrl', undefined);
      } else if (field === 'mediaFile') {
        handleBlockChange(lessonId, blockId, 'mediaFileUrl', undefined);
      }
    }
  };

  const handleRemoveBlock = (lessonId: string, blockId: string) => {
    setLessons(prev => prev.map(lesson => 
      lesson.id === lessonId 
        ? { ...lesson, blocks: lesson.blocks.filter(block => block.id !== blockId) }
        : lesson
    ));
  };

  const handleRemoveLesson = async (lessonId: string) => {
    // Если это режим редактирования и урок существует в базе, удаляем через API
    if (editMode && editCourseId) {
      const lesson = lessons.find(l => l.id === lessonId);
      if (lesson) {
        // Проверяем, является ли это существующим уроком (есть ли он в editCourseData)
        const existingLesson = editCourseData?.lessons?.find((l: any) => String(l.id) === lessonId);
        if (existingLesson) {
          const confirmed = window.confirm(
            'Вы уверены, что хотите удалить этот урок? Это действие нельзя отменить.'
          );
          if (!confirmed) return;

          try {
            await deleteLesson(editCourseId, existingLesson.id);
            // Обновляем данные курса после удаления
            const updatedCourseData = await getLessonById(editCourseId);
            setEditCourseData(updatedCourseData);
            alert('Урок успешно удален');
          } catch (error) {
            console.error('Ошибка при удалении урока:', error);
            alert('Ошибка при удалении урока');
            return;
          }
        }
      }
    }

    // Удаляем урок из состояния
    setLessons(prev => prev.filter(lesson => lesson.id !== lessonId));
    setLessonsCount(prev => Math.max(0, prev - 1));
  };

  // Обработчик удаления изображения курса
  const handleDeleteImage = async (imageId: number) => {
    if (!editMode || !editCourseId) return;

    const confirmed = window.confirm(
      'Вы уверены, что хотите удалить это изображение? Это действие нельзя отменить.'
    );
    if (!confirmed) return;

    try {
      await deleteImage(editCourseId, imageId);
      // Обновляем данные курса после удаления
      const updatedCourseData = await getLessonById(editCourseId);
      setEditCourseData(updatedCourseData);
      alert('Изображение успешно удалено');
    } catch (error) {
      console.error('Ошибка при удалении изображения:', error);
      alert('Ошибка при удалении изображения');
    }
  };

  // Обработчик удаления теста урока
  const handleDeleteLessonTest = async (lessonId: string) => {
    if (!editMode || !editCourseId) return;

    const lesson = lessons.find(l => l.id === lessonId);
    if (!lesson || !lesson.test) return;

    // Находим существующий урок в базе
    const existingLesson = editCourseData?.lessons?.find((l: any) => String(l.id) === lessonId);
    if (!existingLesson || !existingLesson.test) return;

    const confirmed = window.confirm(
      'Вы уверены, что хотите удалить тест этого урока? Это действие нельзя отменить.'
    );
    if (!confirmed) return;

    try {
      await deleteLessonTest(editCourseId, existingLesson.id);
      // Удаляем тест из состояния урока
      setLessons(prev => prev.map(l => 
        l.id === lessonId ? { ...l, test: null } : l
      ));
      // Обновляем данные курса после удаления
      const updatedCourseData = await getLessonById(editCourseId);
      setEditCourseData(updatedCourseData);
      alert('Тест успешно удален');
    } catch (error) {
      console.error('Ошибка при удалении теста:', error);
      alert('Ошибка при удалении теста');
    }
  };

  const handleAddTest = () => {
    const newTest: Test = {
      id: Date.now().toString(),
      questions: [],
      timeToComplete: '10 мин'
    };
    setTests(prev => [...prev, newTest]);
    setTestsCount(prev => prev + 1);
  };

  const handleTestChange = (testId: string, field: keyof Test, value: any) => {
    setTests(prev => prev.map(test => 
      test.id === testId ? { ...test, [field]: value } : test
    ));
  };

  const handleAddQuestion = (testId: string) => {
    const newQuestion: TestQuestion = {
      id: Date.now().toString(),
      type: 'single',
      text: '',
      options: []
    };
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? { ...test, questions: [...test.questions, newQuestion] }
        : test
    ));
  };

  const handleQuestionChange = (testId: string, questionId: string, field: keyof TestQuestion, value: any) => {
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? {
            ...test,
            questions: test.questions.map(q => 
              q.id === questionId ? { ...q, [field]: value } : q
            )
          }
        : test
    ));
  };

  const handleAddAnswerOption = (testId: string, questionId: string) => {
    const newOption: AnswerOption = {
      id: Date.now().toString(),
      text: '',
      isCorrect: false
    };
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? {
            ...test,
            questions: test.questions.map(q => 
              q.id === questionId 
                ? { ...q, options: [...q.options, newOption] }
                : q
            )
          }
        : test
    ));
  };

  const handleOptionChange = (testId: string, questionId: string, optionId: string, field: keyof AnswerOption, value: any) => {
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? {
            ...test,
            questions: test.questions.map(q => 
              q.id === questionId 
                ? {
                    ...q,
                    options: q.options.map(opt => 
                      opt.id === optionId ? { ...opt, [field]: value } : opt
                    )
                  }
                : q
            )
          }
        : test
    ));
  };

  const handleCorrectAnswerChange = (testId: string, questionId: string, optionId: string, isCorrect: boolean) => {
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? {
            ...test,
            questions: test.questions.map(q => {
              if (q.id !== questionId) return q;
              
              // Для типа "single" сбрасываем все остальные
              if (q.type === 'single' && isCorrect) {
                return {
                  ...q,
                  options: q.options.map(opt => 
                    opt.id === optionId ? { ...opt, isCorrect: true } : { ...opt, isCorrect: false }
                  )
                };
              }
              
              // Для типа "multiple" просто переключаем
              return {
                ...q,
                options: q.options.map(opt => 
                  opt.id === optionId ? { ...opt, isCorrect } : opt
                )
              };
            })
          }
        : test
    ));
  };

  const handleRemoveQuestion = (testId: string, questionId: string) => {
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? { ...test, questions: test.questions.filter(q => q.id !== questionId) }
        : test
    ));
  };

  const handleRemoveOption = (testId: string, questionId: string, optionId: string) => {
    setTests(prev => prev.map(test => 
      test.id === testId 
        ? {
            ...test,
            questions: test.questions.map(q => 
              q.id === questionId 
                ? { ...q, options: q.options.filter(opt => opt.id !== optionId) }
                : q
            )
          }
        : test
    ));
  };

  const handleRemoveTest = (testId: string) => {
    setTests(prev => prev.filter(test => test.id !== testId));
    setTestsCount(prev => Math.max(0, prev - 1));
  };

  // Standalone lesson handlers
  const handleStandaloneLessonChange = (field: keyof Lesson, value: any) => {
    if (!standaloneLesson) {
      const newLesson: Lesson = {
        id: Date.now().toString(),
        contentType: 'video',
        lessonName: '',
        difficulty: '',
        duration: '',
        downloadMaterial: null,
        blocks: []
      };
      setStandaloneLesson(newLesson);
      return;
    }
    setStandaloneLesson({ ...standaloneLesson, [field]: value });
  };

  const handleStandaloneLessonBlockChange = (blockId: string, field: keyof LessonBlock, value: any) => {
    if (!standaloneLesson) return;
    setStandaloneLesson({
      ...standaloneLesson,
      blocks: standaloneLesson.blocks.map(block =>
        block.id === blockId ? { ...block, [field]: value } : block
      )
    });
  };

  const handleAddStandaloneLessonBlock = () => {
    if (!standaloneLesson) {
      const newLesson: Lesson = {
        id: Date.now().toString(),
        contentType: 'video',
        lessonName: '',
        difficulty: '',
        duration: '',
        downloadMaterial: null,
        blocks: []
      };
      setStandaloneLesson(newLesson);
      return;
    }
    const newBlock: LessonBlock = {
      id: Date.now().toString()
    };
    setStandaloneLesson({
      ...standaloneLesson,
      blocks: [...standaloneLesson.blocks, newBlock]
    });
  };

  const handleRemoveStandaloneLessonBlock = (blockId: string) => {
    if (!standaloneLesson) return;
    setStandaloneLesson({
      ...standaloneLesson,
      blocks: standaloneLesson.blocks.filter(block => block.id !== blockId)
    });
  };

  const handleStandaloneLessonFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && standaloneLesson) {
      handleStandaloneLessonChange('downloadMaterial', file);
      // Очищаем URL при выборе нового файла
      handleStandaloneLessonChange('downloadMaterialUrl', undefined);
    }
  };

  const handleStandaloneLessonBlockFileSelect = (blockId: string, field: 'lessonFile' | 'mediaFile', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleStandaloneLessonBlockChange(blockId, field, file);
      // Очищаем URL при выборе нового файла
      if (field === 'lessonFile') {
        handleStandaloneLessonBlockChange(blockId, 'lessonFileUrl', undefined);
      } else if (field === 'mediaFile') {
        handleStandaloneLessonBlockChange(blockId, 'mediaFileUrl', undefined);
      }
    }
  };

  // Standalone test handlers
  const handleStandaloneTestChange = (field: keyof Test, value: any) => {
    if (!standaloneTest) {
      const newTest: Test = {
        id: Date.now().toString(),
        questions: [],
        timeToComplete: '10 мин'
      };
      setStandaloneTest(newTest);
      return;
    }
    setStandaloneTest({ ...standaloneTest, [field]: value });
  };

  const handleAddStandaloneTestQuestion = () => {
    if (!standaloneTest) {
      const newTest: Test = {
        id: Date.now().toString(),
        questions: [],
        timeToComplete: '10 мин'
      };
      setStandaloneTest(newTest);
      return;
    }
    const newQuestion: TestQuestion = {
      id: Date.now().toString(),
      type: 'single',
      text: '',
      options: []
    };
    setStandaloneTest({
      ...standaloneTest,
      questions: [...standaloneTest.questions, newQuestion]
    });
  };

  const handleStandaloneTestQuestionChange = (questionId: string, field: keyof TestQuestion, value: any) => {
    if (!standaloneTest) return;
    setStandaloneTest({
      ...standaloneTest,
      questions: standaloneTest.questions.map(q =>
        q.id === questionId ? { ...q, [field]: value } : q
      )
    });
  };

  const handleAddStandaloneTestAnswerOption = (questionId: string) => {
    if (!standaloneTest) return;
    const newOption: AnswerOption = {
      id: Date.now().toString(),
      text: '',
      isCorrect: false
    };
    setStandaloneTest({
      ...standaloneTest,
      questions: standaloneTest.questions.map(q =>
        q.id === questionId
          ? { ...q, options: [...q.options, newOption] }
          : q
      )
    });
  };

  const handleStandaloneTestOptionChange = (questionId: string, optionId: string, field: keyof AnswerOption, value: any) => {
    if (!standaloneTest) return;
    setStandaloneTest({
      ...standaloneTest,
      questions: standaloneTest.questions.map(q =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map(opt =>
                opt.id === optionId ? { ...opt, [field]: value } : opt
              )
            }
          : q
      )
    });
  };

  const handleStandaloneTestCorrectAnswerChange = (questionId: string, optionId: string, isCorrect: boolean) => {
    if (!standaloneTest) return;
    setStandaloneTest({
      ...standaloneTest,
      questions: standaloneTest.questions.map(q => {
        if (q.id !== questionId) return q;
        
        if (q.type === 'single' && isCorrect) {
          return {
            ...q,
            options: q.options.map(opt =>
              opt.id === optionId ? { ...opt, isCorrect: true } : { ...opt, isCorrect: false }
            )
          };
        }
        
        return {
          ...q,
          options: q.options.map(opt =>
            opt.id === optionId ? { ...opt, isCorrect } : opt
          )
        };
      })
    });
  };

  const handleRemoveStandaloneTestQuestion = (questionId: string) => {
    if (!standaloneTest) return;
    setStandaloneTest({
      ...standaloneTest,
      questions: standaloneTest.questions.filter(q => q.id !== questionId)
    });
  };

  const handleRemoveStandaloneTestOption = (questionId: string, optionId: string) => {
    if (!standaloneTest) return;
    setStandaloneTest({
      ...standaloneTest,
      questions: standaloneTest.questions.map(q =>
        q.id === questionId
          ? { ...q, options: q.options.filter(opt => opt.id !== optionId) }
          : q
      )
    });
  };

  // Функция для преобразования difficulty из формата формы в формат API
  const mapDifficultyToApi = (difficulty: string): string => {
    const difficultyMap: Record<string, string> = {
      'easy': 'легкая',
      'medium': 'средняя',
      'hard': 'сложная',
    };
    return difficultyMap[difficulty] || difficulty || 'легкая';
  };

  // Функция для преобразования difficulty из формата API в формат формы
  const mapDifficultyFromApi = (difficulty: string): string => {
    const difficultyMap: Record<string, string> = {
      'легкая': 'easy',
      'средняя': 'medium',
      'сложная': 'hard',
    };
    return difficultyMap[difficulty] || difficulty || 'easy';
  };

  const handlePreview = () => {
    // TODO: Реализовать предпросмотр
    console.log('Preview');
  };

  const handlePublish = async () => {
    if (isPublishing) return;

    try {
      setIsPublishing(true);

      if (activeTab === 'training') {
        // Валидация для Training
        if (!userData?.id) {
          alert('Ошибка: не удалось получить данные пользователя');
          return;
        }

        if (materialType === 'course') {
          // Валидация для курса
          if (!courseName.trim()) {
            alert('Пожалуйста, заполните название курса');
            return;
          }
          if (!courseDescription.trim()) {
            alert('Пожалуйста, заполните описание курса');
            return;
          }

          // Формируем теги
          const tags: string[] = [];
          tags.push(trainingType);
          
          // Формируем access_level
          // Проверяем, выбрано ли "Все" или выбраны все пользователи
          const hasAllSelected = accessLevel.some(level => level.value === ALL_USERS_VALUE);
          const allUserIds = users.map(user => user.value);
          const selectedUserIds = accessLevel
            .filter(level => typeof level.value === 'number')
            .map(level => level.value as number);
          
          const accessLevelValues = hasAllSelected || 
            (selectedUserIds.length === allUserIds.length && 
             allUserIds.every(id => selectedUserIds.includes(id)))
            ? allUserIds
            : selectedUserIds;
          
          if (accessLevelValues.length > 0) {
            tags.push(...accessLevelValues.map(id => `access_${id}`));
          }
          
          // Добавляем пользовательские теги из CreatableSelect
          if (courseTags.length > 0) {
            const customTags = courseTags
              .map(tag => tag.value.trim())
              .filter(tag => tag.length > 0 && !tag.startsWith('access_') && tag !== 'organizational' && tag !== 'profile');
            tags.push(...customTags);
          }

          // Создаем запись курса в базе знаний
          const knowledgeData: CreateKnowledgeEntryRequest = {
            user_id: String(userData.id),
            type: trainingType,
            name: courseName,
            description: courseDescription,
            goal: courseGoal,
            tags,
            quiz: [],
            access_level: accessLevelValues,
          };

          let knowledgeId: number;
          
          if (editMode && editCourseId) {
            // Режим редактирования
            const updateData: UpdateKnowledgeEntryRequest = {
              type: trainingType,
              name: courseName,
              description: courseDescription,
              goal: courseGoal,
              tags,
              access_level: accessLevelValues,
            };
            const updatedKnowledge = await updateKnowledgeEntry(editCourseId, updateData);
            console.log('Обновлен курс:', updatedKnowledge);
            knowledgeId = updatedKnowledge.id;
          } else {
            // Режим создания
            const createdKnowledge = await createKnowledgeEntry(knowledgeData);
            console.log('Создан курс:', createdKnowledge);
            knowledgeId = createdKnowledge.id;
          }

          // Загружаем обложку, если есть
          if (coverFile) {
            try {
              const uploadedImage = await uploadImage(knowledgeId, coverFile);
              console.log('Загружена обложка курса:', uploadedImage);
            } catch (error) {
              console.error('Ошибка при загрузке обложки:', error);
            }
          }

          // Обновляем или создаем уроки для курса
          if (editMode && editCourseData) {
            // В режиме редактирования обновляем существующие уроки или создаем новые
            const existingLessons = editCourseData.lessons || [];
            
            for (let i = 0; i < lessons.length; i++) {
              const lesson = lessons[i];
              try {
                // Формируем текст урока
                const lessonTextParts: string[] = [];
                if (lesson.lessonName) lessonTextParts.push(lesson.lessonName);
                if (lesson.difficulty) lessonTextParts.push(`Сложность: ${lesson.difficulty}`);
                if (lesson.duration) lessonTextParts.push(`Длительность: ${lesson.duration}`);
                
                // Добавляем блоки урока
                lesson.blocks.forEach((block, index) => {
                  if (block.text) {
                    lessonTextParts.push(`\nБлок ${index + 1}: ${block.text}`);
                  }
                  if (block.videoLink) {
                    lessonTextParts.push(`Видео: ${block.videoLink}`);
                  }
                });

                const lessonText = lessonTextParts.join('\n');

                // image урока оставляем пустым - файлы загружаются отдельно после создания урока
                let lessonImageUrl = '';

                // Преобразуем блоки в формат API
                const apiBlocks: CreateBlockRequest[] = lesson.blocks.map((block, index) => {
                  // Определяем тип блока на основе block.type
                  // API принимает только 'video', 'file' или 'media'
                  let blockType = 'media'; // По умолчанию 'media'
                  if (block.type === 'video') {
                    blockType = 'video';
                  } else if (block.type === 'file') {
                    blockType = 'file';
                  } else if (block.type === 'text-media') {
                    blockType = 'media'; // 'text-media' сохраняется как 'media' в API
                  }
                  
                  const apiBlock: CreateBlockRequest = {
                    type: blockType,
                    text: block.text || '',
                    video_url: block.videoLink || '',
                    file_url: '', // Будет заполнено после загрузки файла
                    media_url: '', // Будет заполнено после загрузки медиа
                    order: index + 1,
                  };
                  return apiBlock;
                });

                // Преобразуем difficulty в формат API
                const apiDifficulty = mapDifficultyToApi(lesson.difficulty);

                // Проверяем, существует ли урок
                const existingLesson = existingLessons[i];
                let lessonId: number;
                if (existingLesson) {
                  // Обновляем существующий урок
                  await updateLesson(knowledgeId, existingLesson.id, {
                    text: lessonText,
                    image: lessonImageUrl,
                  });
                  lessonId = existingLesson.id;
                } else {
                  // Создаем новый урок
                  const createdLesson = await createLesson(knowledgeId, {
                    text: lessonText,
                    image: lessonImageUrl,
                    duration: lesson.duration || '',
                    difficulty: apiDifficulty,
                    is_completed: false,
                    blocks: apiBlocks,
                  });
                  lessonId = createdLesson.id;
                }

                // В режиме редактирования берём тест из массива tests (там актуальные правки пользователя, в т.ч. isCorrect)
                const testIndex = i - (lessons.length - tests.length);
                const effectiveTest = (testIndex >= 0 && testIndex < tests.length) ? tests[testIndex] : lesson.test;

                // Создаем тест для урока, если он есть
                // Для существующих уроков проверяем, есть ли уже тест
                if (effectiveTest && effectiveTest.questions.length > 0) {
                  try {
                    let testExists = false;
                    let existingTest: Awaited<ReturnType<typeof getLessonTest>> | null = null;

                    // Если это существующий урок, проверяем наличие теста
                    if (existingLesson) {
                      try {
                        existingTest = await getLessonTest(knowledgeId, lessonId);
                        if (existingTest && existingTest.id) {
                          testExists = true;
                          console.log(`Тест уже существует для урока ${lessonId}, пропускаем создание`);
                        }
                      } catch (error) {
                        // Если тест не найден (404), значит его нет - создаем новый
                        console.log(`Тест не найден для урока ${lessonId}, создаем новый`);
                      }
                    }

                    // Если тест уже есть — синхронизируем is_correct из формы (массив tests)
                    if (testExists && existingTest && existingTest.questions) {
                      for (let qIdx = 0; qIdx < Math.min(existingTest.questions.length, effectiveTest.questions.length); qIdx++) {
                        const apiQuestion = existingTest.questions[qIdx];
                        const formQuestion = effectiveTest.questions[qIdx];
                        const apiOptions = apiQuestion.answer_options || [];
                        const formOptions = formQuestion.options || [];
                        for (const formOpt of formOptions) {
                          const apiOpt = apiOptions.find((o: { id: number }) => String(o.id) === String(formOpt.id));
                          if (apiOpt && formOpt.isCorrect !== undefined && apiOpt.is_correct !== formOpt.isCorrect) {
                            try {
                              await updateAnswerOption(knowledgeId, lessonId, apiQuestion.id, formOpt.id, {
                                is_correct: formOpt.isCorrect,
                              });
                            } catch (err) {
                              console.error(`Ошибка обновления варианта ответа ${formOpt.id}:`, err);
                            }
                          }
                        }
                      }
                    }

                    // Создаем тест только если его нет
                    if (!testExists) {
                      // Создаем тест
                      const createdTest = await createLessonTest(knowledgeId, lessonId, {
                        time_limit: effectiveTest.timeToComplete,
                        questions: [],
                      });

                      // Создаем вопросы и варианты ответов
                      for (let qIndex = 0; qIndex < effectiveTest.questions.length; qIndex++) {
                        const question = effectiveTest.questions[qIndex];
                        
                        // Преобразуем тип вопроса
                        let questionType = 'single';
                        if (question.type === 'multiple') {
                          questionType = 'multiple';
                        } else if (question.type === 'open') {
                          questionType = 'open';
                        }

                        // Создаем вопрос
                        const createdQuestion = await createTestQuestion(knowledgeId, lessonId, {
                          text: question.text,
                          type: questionType,
                          order: qIndex + 1,
                          answer_options: [],
                        });

                        // Создаем варианты ответов
                        if (question.options && question.options.length > 0) {
                          for (let oIndex = 0; oIndex < question.options.length; oIndex++) {
                            const option = question.options[oIndex];
                            const optionText = option.text || '';
                            if (!optionText || optionText.trim() === '') {
                              console.warn(`Пропускаем вариант ответа ${oIndex + 1} - нет текста`);
                              continue;
                            }
                            await createAnswerOption(knowledgeId, lessonId, createdQuestion.id, {
                              text: optionText,
                              is_correct: option.isCorrect !== undefined ? option.isCorrect : false,
                              order: oIndex + 1,
                            });
                          }
                        }
                      }
                    }
                  } catch (error: any) {
                    // Если ошибка "Test already exists", просто пропускаем
                    if (error.message && error.message.includes('already exists')) {
                      console.log(`Тест уже существует для урока ${lessonId}, пропускаем создание`);
                    } else {
                      console.error(`Ошибка при создании теста для урока ${lesson.id}:`, error);
                    }
                  }
                }
              } catch (error) {
                console.error(`Ошибка при обработке урока ${lesson.id}:`, error);
              }
            }
          } else {
            // Режим создания - создаем новые уроки
            
            // Проверяем, что knowledgeId установлен
            if (!knowledgeId) {
              console.error('Ошибка: knowledgeId не установлен перед созданием уроков');
              alert('Ошибка: не удалось получить ID созданного курса. Пожалуйста, попробуйте еще раз.');
              setIsPublishing(false);
              return;
            }
            
            console.log('Создание уроков для курса:', {
              knowledgeId,
              lessonsCount: lessons.length,
              testsCount: tests.length,
            });
            
            // Привязываем тесты из массива tests к урокам
            // Если тестов меньше чем уроков, привязываем тесты к последним урокам
            console.log('Привязка тестов к урокам:', {
              testsCount: tests.length,
              lessonsCount: lessons.length,
              tests: tests.map(t => ({ id: t.id, questionsCount: t.questions?.length || 0 })),
            });
            
            const lessonsWithTests = lessons.map((lesson, index) => {
              // Вычисляем индекс теста для этого урока
              // Если тестов меньше чем уроков, привязываем тесты к последним урокам
              const testIndex = tests.length <= lessons.length 
                ? index - (lessons.length - tests.length)
                : index;
              
              // Если индекс валидный и есть тест, привязываем его
              if (testIndex >= 0 && testIndex < tests.length) {
                console.log(`Привязываем тест ${testIndex} к уроку ${index} (${lesson.id})`);
                return {
                  ...lesson,
                  test: tests[testIndex],
                };
              }
              return lesson;
            });
            
            console.log('Уроки с тестами:', lessonsWithTests.map(l => ({
              id: l.id,
              hasTest: !!l.test,
              testQuestionsCount: l.test?.questions?.length || 0,
            })));

            for (const lesson of lessonsWithTests) {
              try {
              // Формируем текст урока
              const lessonTextParts: string[] = [];
              if (lesson.lessonName) lessonTextParts.push(lesson.lessonName);
              if (lesson.difficulty) lessonTextParts.push(`Сложность: ${lesson.difficulty}`);
              if (lesson.duration) lessonTextParts.push(`Длительность: ${lesson.duration}`);
              
              // Добавляем блоки урока
              lesson.blocks.forEach((block, index) => {
                if (block.text) {
                  lessonTextParts.push(`\nБлок ${index + 1}: ${block.text}`);
                }
                if (block.videoLink) {
                  lessonTextParts.push(`Видео: ${block.videoLink}`);
                }
              });

              const lessonText = lessonTextParts.join('\n');

              // image урока оставляем пустым - файлы загружаются отдельно после создания урока
              let lessonImageUrl = '';

              // Преобразуем блоки в формат API
              const apiBlocks: CreateBlockRequest[] = lesson.blocks.map((block, index) => {
                // Определяем тип блока на основе block.type
                // API принимает только 'video', 'file' или 'media'
                let blockType = 'media'; // По умолчанию 'media'
                if (block.type === 'video') {
                  blockType = 'video';
                } else if (block.type === 'file') {
                  blockType = 'file';
                } else if (block.type === 'text-media') {
                  blockType = 'media'; // 'text-media' сохраняется как 'media' в API
                }
                
                const apiBlock: CreateBlockRequest = {
                  type: blockType,
                  text: block.text || '',
                  video_url: block.videoLink || '',
                  file_url: '', // Будет заполнено после загрузки файла
                  media_url: '', // Будет заполнено после загрузки медиа
                  order: index + 1,
                };
                return apiBlock;
              });

              // Преобразуем difficulty в формат API
              const apiDifficulty = mapDifficultyToApi(lesson.difficulty);

              // Загружаем material_file через uploadImage для knowledgeId (если нужно)
              // material_file будет загружен после создания урока через uploadLessonMaterial
              let materialFileUrl = '';

              // Создаем урок со всеми данными
              const createdLesson = await createLesson(knowledgeId, {
                text: lessonText,
                image: lessonImageUrl,
                duration: lesson.duration || '',
                difficulty: apiDifficulty,
                material_file: materialFileUrl, // Пустой при создании, загрузим после
                is_completed: false,
                blocks: apiBlocks,
              });

              // Загружаем material_file после создания урока только если не все блоки типа 'text-media'
              const allBlocksAreTextMedia = lesson.blocks.length > 0 && lesson.blocks.every(block => block.type === 'text-media');
              if (!allBlocksAreTextMedia && lesson.downloadMaterial && createdLesson.id && lesson.downloadMaterial instanceof File) {
                try {
                  const materialUrl = await uploadLessonMaterial(knowledgeId, createdLesson.id, lesson.downloadMaterial);
                  console.log('Material file загружен:', materialUrl);
                  // Обновляем урок, добавляя URL в массив material_file
                  const currentMaterialFiles = Array.isArray(createdLesson.material_file) 
                    ? createdLesson.material_file 
                    : (createdLesson.material_file ? [createdLesson.material_file] : []);
                  await updateLesson(knowledgeId, createdLesson.id, {
                    material_file: [...currentMaterialFiles, materialUrl]
                  });
                } catch (error) {
                  console.error('Ошибка при загрузке material_file:', error);
                  // Не блокируем создание урока из-за ошибки загрузки файла
                }
              }

              // Загружаем файлы блоков после создания урока
              // Блоки создаются вместе с уроком, поэтому нужно получить созданные блоки с их ID
              if (createdLesson.id && createdLesson.blocks && createdLesson.blocks.length > 0) {
                for (let i = 0; i < lesson.blocks.length && i < createdLesson.blocks.length; i++) {
                  const block = lesson.blocks[i];
                  const createdBlock = createdLesson.blocks[i];
                  
                  try {
                    // Для файлов блоков используем uploadBlockMedia с ID блока
                    if (block.lessonFile && createdBlock.id && block.lessonFile instanceof File) {
                      const fileUrl = await uploadBlockMedia(knowledgeId, createdLesson.id, createdBlock.id, block.lessonFile);
                      console.log(`Файл блока ${i + 1} загружен:`, fileUrl);
                    }
                    if (block.mediaFile && createdBlock.id && block.mediaFile instanceof File) {
                      const mediaUrl = await uploadBlockMedia(knowledgeId, createdLesson.id, createdBlock.id, block.mediaFile);
                      console.log(`Медиа блока ${i + 1} загружено:`, mediaUrl);
                    }
                  } catch (error) {
                    console.error(`Ошибка при загрузке файлов блока ${i + 1}:`, error);
                    // Не блокируем создание урока из-за ошибки загрузки файла
                  }
                }
              }

              // Создаем тест для урока, если он есть
              if (lesson.test && lesson.test.questions && lesson.test.questions.length > 0 && createdLesson.id) {
                try {
                  console.log(`Создаем тест для урока ${lesson.id}:`, lesson.test);
                  
                  // Создаем тест
                  const createdTest = await createLessonTest(knowledgeId, createdLesson.id, {
                    time_limit: lesson.test.timeToComplete || '10-15 минут',
                    questions: [],
                  });

                  console.log(`Тест создан с ID: ${createdTest.id}`);

                  // Создаем вопросы и варианты ответов
                  for (let qIndex = 0; qIndex < lesson.test.questions.length; qIndex++) {
                    const question = lesson.test.questions[qIndex];
                    
                    if (!question.text || question.text.trim() === '') {
                      console.warn(`Пропускаем вопрос ${qIndex + 1} - нет текста`);
                      continue;
                    }
                    
                    // Преобразуем тип вопроса
                    let questionType = 'single';
                    if (question.type === 'multiple') {
                      questionType = 'multiple';
                    } else if (question.type === 'open') {
                      questionType = 'open';
                    }

                    // Создаем вопрос
                    const createdQuestion = await createTestQuestion(knowledgeId, createdLesson.id, {
                      text: question.text,
                      type: questionType,
                      order: qIndex + 1,
                      answer_options: [],
                    });

                    console.log(`Вопрос создан с ID: ${createdQuestion.id}`);

                    // Создаем варианты ответов
                    if (question.options && question.options.length > 0) {
                      console.log(`Создаем варианты ответов для вопроса ${qIndex + 1}:`, question.options);
                      for (let oIndex = 0; oIndex < question.options.length; oIndex++) {
                        const option = question.options[oIndex];
                        console.log(`Вариант ответа ${oIndex + 1}:`, option);
                        
                        // Проверяем наличие текста
                        const optionText = option.text || '';
                        if (!optionText || optionText.trim() === '') {
                          console.warn(`Пропускаем вариант ответа ${oIndex + 1} - нет текста. Данные:`, option);
                          continue;
                        }
                        
                        try {
                          await createAnswerOption(knowledgeId, createdLesson.id, createdQuestion.id, {
                            text: optionText,
                            is_correct: option.isCorrect !== undefined ? option.isCorrect : false,
                            order: oIndex + 1,
                          });
                          console.log(`Вариант ответа ${oIndex + 1} создан`);
                  } catch (error) {
                          console.error(`Ошибка при создании варианта ответа ${oIndex + 1}:`, error);
                        }
                      }
                    } else {
                      console.warn(`Нет вариантов ответов для вопроса ${qIndex + 1}`);
                    }
                  }
                  
                  console.log(`Тест для урока ${lesson.id} успешно создан`);
                } catch (error) {
                  console.error(`Ошибка при создании теста для урока ${lesson.id}:`, error);
                }
              } else {
                console.log(`Тест не создается для урока ${lesson.id}:`, {
                  hasTest: !!lesson.test,
                  hasQuestions: (lesson.test?.questions?.length ?? 0) > 0,
                  hasLessonId: !!createdLesson.id,
                });
              }
              } catch (error: any) {
                console.error(`Ошибка при создании урока ${lesson.id}:`, error);
                // Более информативное сообщение об ошибке
                const errorMessage = error?.message || 'Неизвестная ошибка';
                if (errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('Access denied')) {
                  alert(`Ошибка доступа при создании урока "${lesson.lessonName || 'без названия'}": недостаточно прав для добавления урока к курсу. Возможно, курс был создан другим пользователем или требуется дополнительная авторизация.`);
                } else {
                  alert(`Ошибка при создании урока "${lesson.lessonName || 'без названия'}": ${errorMessage}`);
                }
                // Продолжаем обработку остальных уроков, но выводим ошибку
              }
            }
          }

          alert(editMode ? 'Курс успешно обновлен!' : 'Курс успешно опубликован!');
          navigate('/knowledge-base/admin');

        } else if (materialType === 'lesson') {
          // Валидация для отдельного урока
          if (!selectedCourseForLesson) {
            alert('Пожалуйста, выберите курс для урока');
            return;
          }
          if (!standaloneLesson) {
            alert('Пожалуйста, заполните данные урока');
            return;
          }
          if (!standaloneLesson.lessonName.trim()) {
            alert('Пожалуйста, заполните название урока');
            return;
          }

          // Формируем текст урока
          const lessonTextParts: string[] = [];
          lessonTextParts.push(standaloneLesson.lessonName);
          if (standaloneLesson.difficulty) lessonTextParts.push(`Сложность: ${standaloneLesson.difficulty}`);
          if (standaloneLesson.duration) lessonTextParts.push(`Длительность: ${standaloneLesson.duration}`);
          
          // Добавляем блоки урока
          standaloneLesson.blocks.forEach((block, index) => {
            if (block.text) {
              lessonTextParts.push(`\nБлок ${index + 1}: ${block.text}`);
            }
            if (block.videoLink) {
              lessonTextParts.push(`Видео: ${block.videoLink}`);
            }
          });

          const lessonText = lessonTextParts.join('\n');

          // image урока оставляем пустым - файлы загружаются отдельно после создания урока
          let lessonImageUrl = '';

          // Преобразуем блоки в формат API
          const apiBlocks: CreateBlockRequest[] = standaloneLesson.blocks.map((block, index) => {
            // Определяем тип блока на основе block.type
            // API принимает только 'video', 'file' или 'media'
            let blockType = 'media'; // По умолчанию 'media'
            if (block.type === 'video') {
              blockType = 'video';
            } else if (block.type === 'file') {
              blockType = 'file';
            } else if (block.type === 'text-media') {
              blockType = 'media'; // 'text-media' сохраняется как 'media' в API
            }
            
            const apiBlock: CreateBlockRequest = {
              type: blockType,
              text: block.text || '',
              video_url: block.videoLink || '',
              file_url: '', // Будет заполнено после загрузки файла
              media_url: '', // Будет заполнено после загрузки медиа
              order: index + 1,
            };
            return apiBlock;
          });

          // Преобразуем difficulty в формат API
          const apiDifficulty = mapDifficultyToApi(standaloneLesson.difficulty);

          // Создаем урок
          const createdLesson = await createLesson(selectedCourseForLesson.value, {
            text: lessonText,
            image: lessonImageUrl,
            duration: standaloneLesson.duration || '',
            difficulty: apiDifficulty,
            material_file: [], // Пустой при создании, загрузим после
            is_completed: false,
            blocks: apiBlocks,
          });

          // Загружаем material_file после создания урока только если не все блоки типа 'text-media'
          const allStandaloneBlocksAreTextMedia = standaloneLesson.blocks.length > 0 && standaloneLesson.blocks.every(block => block.type === 'text-media');
          if (!allStandaloneBlocksAreTextMedia && standaloneLesson.downloadMaterial && createdLesson.id && standaloneLesson.downloadMaterial instanceof File) {
            try {
              const materialUrl = await uploadLessonMaterial(selectedCourseForLesson.value, createdLesson.id, standaloneLesson.downloadMaterial);
              console.log('Material file загружен:', materialUrl);
              // Обновляем урок, добавляя URL в массив material_file
              const currentMaterialFiles = Array.isArray(createdLesson.material_file) 
                ? createdLesson.material_file 
                : (createdLesson.material_file ? [createdLesson.material_file] : []);
              await updateLesson(selectedCourseForLesson.value, createdLesson.id, {
                material_file: [...currentMaterialFiles, materialUrl]
              });
            } catch (error) {
              console.error('Ошибка при загрузке material_file:', error);
              // Не блокируем создание урока из-за ошибки загрузки файла
            }
          }

          // Загружаем файлы блоков после создания урока
          // Блоки создаются вместе с уроком, поэтому нужно получить созданные блоки с их ID
          if (createdLesson.id && createdLesson.blocks && createdLesson.blocks.length > 0) {
            for (let i = 0; i < standaloneLesson.blocks.length && i < createdLesson.blocks.length; i++) {
              const block = standaloneLesson.blocks[i];
              const createdBlock = createdLesson.blocks[i];
              
              try {
                // Для файлов блоков используем uploadBlockMedia с ID блока
                if (block.lessonFile && createdBlock.id && block.lessonFile instanceof File) {
                  const fileUrl = await uploadBlockMedia(selectedCourseForLesson.value, createdLesson.id, createdBlock.id, block.lessonFile);
                  console.log(`Файл блока ${i + 1} загружен:`, fileUrl);
                }
                if (block.mediaFile && createdBlock.id && block.mediaFile instanceof File) {
                  const mediaUrl = await uploadBlockMedia(selectedCourseForLesson.value, createdLesson.id, createdBlock.id, block.mediaFile);
                  console.log(`Медиа блока ${i + 1} загружено:`, mediaUrl);
                }
              } catch (error) {
                console.error(`Ошибка при загрузке файлов блока ${i + 1}:`, error);
                // Не блокируем создание урока из-за ошибки загрузки файла
              }
            }
          }

          // Создаем тест для урока, если он есть
          if (standaloneLesson.test && standaloneLesson.test.questions.length > 0 && createdLesson.id) {
            try {
              // Создаем тест
              const createdTest = await createLessonTest(selectedCourseForLesson.value, createdLesson.id, {
                time_limit: standaloneLesson.test.timeToComplete,
                questions: [],
              });

              // Создаем вопросы и варианты ответов
              for (let qIndex = 0; qIndex < standaloneLesson.test.questions.length; qIndex++) {
                const question = standaloneLesson.test.questions[qIndex];
                
                // Преобразуем тип вопроса
                let questionType = 'single';
                if (question.type === 'multiple') {
                  questionType = 'multiple';
                } else if (question.type === 'open') {
                  questionType = 'open';
                }

                // Создаем вопрос
                const createdQuestion = await createTestQuestion(selectedCourseForLesson.value, createdLesson.id, {
                  text: question.text,
                  type: questionType,
                  order: qIndex + 1,
                  answer_options: [],
                });

                // Создаем варианты ответов
                if (question.options && question.options.length > 0) {
                  for (let oIndex = 0; oIndex < question.options.length; oIndex++) {
                    const option = question.options[oIndex];
                    const optionText = option.text || '';
                    if (!optionText || optionText.trim() === '') {
                      console.warn(`Пропускаем вариант ответа ${oIndex + 1} - нет текста`);
                      continue;
                    }
                    await createAnswerOption(selectedCourseForLesson.value, createdLesson.id, createdQuestion.id, {
                      text: optionText,
                      is_correct: option.isCorrect !== undefined ? option.isCorrect : false,
                      order: oIndex + 1,
                    });
                  }
                }
              }
            } catch (error) {
              console.error('Ошибка при создании теста для урока:', error);
            }
          }

          alert('Урок успешно опубликован!');
          navigate('/knowledge-base/admin');

        } else if (materialType === 'test') {
          // Валидация для теста
          if (!selectedCourseForTest) {
            alert('Пожалуйста, выберите курс для теста');
            return;
          }
          if (!selectedLessonForTest) {
            alert('Пожалуйста, выберите урок для теста');
            return;
          }
          if (!standaloneTest || standaloneTest.questions.length === 0) {
            alert('Пожалуйста, добавьте хотя бы один вопрос в тест');
            return;
          }

          // Формируем quiz данные из вопросов теста
          const quizData: string[] = [];
          standaloneTest.questions.forEach((question) => {
            const questionData = {
              id: question.id,
              type: question.type,
              text: question.text,
              options: question.options.map(opt => ({
                id: opt.id,
                text: opt.text,
                isCorrect: opt.isCorrect,
              })),
              timeToComplete: standaloneTest.timeToComplete,
            };
            quizData.push(JSON.stringify(questionData));
          });

          // Обновляем запись курса/урока с quiz данными
          // Сначала получаем текущую запись, затем обновляем
          // Для упрощения создаем новую запись с quiz
          const testName = `Тест для урока`;
          const testDescription = `Время на прохождение: ${standaloneTest.timeToComplete}\nКоличество вопросов: ${standaloneTest.questions.length}`;
          
          const knowledgeData: CreateKnowledgeEntryRequest = {
            user_id: String(userData.id),
            type: 'organizational', // или другой тип по необходимости
            name: testName,
            description: testDescription,
            goal: '',
            tags: ['test', `course_${selectedCourseForTest.value}`, `lesson_${selectedLessonForTest.value}`],
            quiz: quizData,
            access_level: [],
          };

          await createKnowledgeEntry(knowledgeData);

          alert('Тест успешно опубликован!');
          navigate('/knowledge-base/admin');
        }

      } else {
        // Knowledge Base - оставляем как было или можно удалить
        alert('Публикация материалов базы знаний пока не реализована');
      }
    } catch (error) {
      console.error('Ошибка при публикации:', error);
      alert(`Ошибка при публикации: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setIsPublishing(false);
    }
  };

  // Загрузка пользователей
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setUsersLoading(true);
        const usersData = await getUsersSummary({
          isActive: true,
          pageSize: 1000
        } as any);
        // Форматируем пользователей для react-select
        const formattedUsers = (usersData.users || []).map((user: any) => ({
          value: user.id,
          label: `${user.surname || ''} ${user.name || ''}`.trim() || `Пользователь ${user.id}`
        }));
        setUsers(formattedUsers);
        
        // Автоматически добавляем автора в доступ, если его там еще нет
        if (userData?.id && !editMode) {
          const authorId = userData.id;
          setAccessLevel((prevAccessLevel) => {
            const authorExists = prevAccessLevel.some(level => level.value === authorId);
            if (!authorExists) {
              const authorOption = formattedUsers.find((u: any) => u.value === authorId);
              if (authorOption) {
                return [authorOption, ...prevAccessLevel];
              } else {
                // Если автора нет в списке пользователей, создаем опцию вручную
                const authorLabel = `${userData.surname || ''} ${userData.name || ''}`.trim() || `Пользователь ${authorId}`;
                return [{ value: authorId, label: authorLabel }, ...prevAccessLevel];
              }
            }
            return prevAccessLevel;
          });
        }
      } catch (error) {
        console.error('Ошибка при загрузке пользователей:', error);
      } finally {
        setUsersLoading(false);
      }
    };

    if (activeTab === 'training' && materialType === 'course') {
      loadUsers();
    }
  }, [activeTab, materialType, userData?.id, editMode, userData?.surname, userData?.name]);

  // Загрузка данных курса при редактировании
  useEffect(() => {
    const loadCourseForEdit = async () => {
      const editId = searchParams.get('edit');
      if (editId) {
        try {
          setLoadingCourseData(true);
          setEditMode(true);
          setEditCourseId(editId);
          
          const courseData = await getLessonById(editId);
          setEditCourseData(courseData);
          
          // Заполняем форму данными из API
          if (courseData.name) {
            setCourseName(courseData.name);
          }
          if (courseData.description) {
            setCourseDescription(courseData.description);
          }
          if (courseData.goal) {
            setCourseGoal(courseData.goal);
          }
          
          // Заполняем теги (исключая служебные теги)
          if (courseData.tags && courseData.tags.length > 0) {
            const customTags = courseData.tags
              .filter(tag => 
                tag !== 'organizational' && 
                tag !== 'profile' && 
                !tag.startsWith('access_')
              )
              .map(tag => ({
                value: tag,
                label: tag.charAt(0).toUpperCase() + tag.slice(1),
              }));
            setCourseTags(customTags);
          }
          
          // Определяем тип обучения по полю type или тегам
          if (courseData.type === 'organizational' || courseData.tags.some(tag => tag.toLowerCase().includes('organizational'))) {
            setTrainingType('organizational');
          } else if (courseData.tags.some(tag => tag.toLowerCase().includes('profile'))) {
            setTrainingType('profile');
          }
          
          // Заполняем access_level из access_level поля (это ID пользователей)
          // Загружаем пользователей, если они еще не загружены
          if (courseData.access_level && courseData.access_level.length > 0) {
            try {
              const usersData = await getUsersSummary({
                isActive: true,
                pageSize: 1000
              } as any);
              const formattedUsers = (usersData.users || []).map((user: any) => ({
                value: user.id,
                label: `${user.surname || ''} ${user.name || ''}`.trim() || `Пользователь ${user.id}`
              }));
              
              // Находим пользователей по ID из access_level
              const accessLevelOptions = courseData.access_level
                .map((userId: number) => {
                  const user = formattedUsers.find((u: any) => u.value === userId);
                  if (user) {
                    return user;
                  }
                  // Если пользователь не найден, создаем опцию с ID
                  return {
                    value: userId,
                    label: `Пользователь ${userId}`
                  };
                })
                .filter(Boolean);
              
              setAccessLevel(accessLevelOptions);
              
              // Автоматически добавляем автора, если его нет в списке
              if (userData?.id) {
                const authorId = userData.id;
                const authorExists = accessLevelOptions.some(opt => opt.value === authorId);
                if (!authorExists) {
                  const authorOption = formattedUsers.find((u: any) => u.value === authorId);
                  if (authorOption) {
                    setAccessLevel([authorOption, ...accessLevelOptions]);
                  } else {
                    const authorLabel = `${userData.surname || ''} ${userData.name || ''}`.trim() || `Пользователь ${authorId}`;
                    setAccessLevel([{ value: authorId, label: authorLabel }, ...accessLevelOptions]);
                  }
                }
              }
            } catch (error) {
              console.error('Ошибка при загрузке пользователей для access_level:', error);
              // Если не удалось загрузить, используем ID как есть
              const accessLevelOptions = courseData.access_level.map((userId: number) => ({
                value: userId,
                label: `Пользователь ${userId}`
              }));
              setAccessLevel(accessLevelOptions);
              
              // Автоматически добавляем автора, если его нет в списке
              if (userData?.id) {
                const authorId = userData.id;
                const authorExists = accessLevelOptions.some(opt => opt.value === authorId);
                if (!authorExists) {
                  const authorLabel = `${userData.surname || ''} ${userData.name || ''}`.trim() || `Пользователь ${authorId}`;
                  setAccessLevel([{ value: authorId, label: authorLabel }, ...accessLevelOptions]);
                }
              }
            }
          } else {
            // Если access_level пустой, добавляем только автора
            if (userData?.id) {
              try {
                const usersData = await getUsersSummary({
                  isActive: true,
                  pageSize: 1000
                } as any);
                const formattedUsers = (usersData.users || []).map((user: any) => ({
                  value: user.id,
                  label: `${user.surname || ''} ${user.name || ''}`.trim() || `Пользователь ${user.id}`
                }));
                const authorOption = formattedUsers.find((u: any) => u.value === userData.id);
                if (authorOption) {
                  setAccessLevel([authorOption]);
                } else {
                  const authorLabel = `${userData.surname || ''} ${userData.name || ''}`.trim() || `Пользователь ${userData.id}`;
                  setAccessLevel([{ value: userData.id, label: authorLabel }]);
                }
              } catch (error) {
                console.error('Ошибка при загрузке пользователей для автора:', error);
                const authorLabel = `${userData.surname || ''} ${userData.name || ''}`.trim() || `Пользователь ${userData.id}`;
                setAccessLevel([{ value: userData.id, label: authorLabel }]);
              }
            }
          }
          
          // Загружаем уроки курса
          if (courseData.lessons && courseData.lessons.length > 0) {
            // Преобразуем уроки из API в формат формы
            const formattedLessons: Lesson[] = await Promise.all(
              courseData.lessons.map(async (apiLesson, index) => {
              // Используем text из ApiLessonItem (урок имеет поле text)
              const lessonName = apiLesson.text ? apiLesson.text.split('\n').filter((line: string) => line.trim())[0] : `Урок ${index + 1}`;
                
                // Преобразуем блоки
                const formattedBlocks: LessonBlock[] = (apiLesson.blocks || []).map((block, blockIndex) => {
                  // Определяем тип блока на основе данных из API
                  let blockType: 'video' | 'file' | 'text-media' = 'video';
                  if (block.type === 'video') {
                    blockType = 'video';
                  } else if (block.type === 'file') {
                    blockType = 'file';
                  } else if (block.type === 'media') {
                    // Если есть media_url или file_url, это может быть 'text-media' или 'file'
                    blockType = block.media_url ? 'text-media' : 'file';
                  }
                  
                  return {
                    id: String(block.id || blockIndex),
                    type: blockType,
                    text: block.text || '',
                    videoLink: block.video_url || '',
                    lessonFile: null,
                    lessonFileUrl: block.file_url || undefined, // Сохраняем URL файла
                    mediaFile: null,
                    mediaFileUrl: block.media_url || undefined, // Сохраняем URL медиа
                  };
                });

                // Загружаем тест, если есть
                let lessonTest: Test | null = null;
                if (apiLesson.test) {
                  try {
                    const testData = apiLesson.test;
                    lessonTest = {
                      id: String(testData.id),
                      timeToComplete: testData.time_limit || '10-15 минут',
                      questions: (testData.questions || []).map((q: any) => ({
                        id: String(q.id),
                        type: q.type === 'multiple' ? 'multiple' : (q.type === 'open' ? 'open' : 'single') as 'single' | 'multiple' | 'open',
                        text: q.text || '',
                        options: (q.answer_options || []).map((opt: any) => ({
                          id: String(opt.id),
                          text: opt.text || '',
                          isCorrect: opt.is_correct || false,
                        })),
                      })),
                    };
                  } catch (error) {
                    console.error(`Ошибка при загрузке теста для урока ${apiLesson.id}:`, error);
                  }
                }

              return {
                id: String(apiLesson.id),
                contentType: 'video' as const, // Оставляем для обратной совместимости, но не используем
                lessonName: lessonName || `Урок ${index + 1}`,
                  difficulty: mapDifficultyFromApi(apiLesson.difficulty || 'легкая'),
                duration: apiLesson.duration || '',
                downloadMaterial: null,
                downloadMaterialUrl: Array.isArray(apiLesson.material_file) && apiLesson.material_file.length > 0 
                  ? apiLesson.material_file[0] 
                  : (typeof apiLesson.material_file === 'string' ? apiLesson.material_file : undefined), // Сохраняем URL материала для скачивания
                  blocks: formattedBlocks,
                  test: lessonTest,
              };
              })
            );
            setLessons(formattedLessons);
            setLessonsCount(formattedLessons.length);

            // Загружаем тесты из уроков в массив tests для отображения в разделе "Тест после прохождения курса"
            const testsFromLessons: Test[] = [];
            formattedLessons.forEach((lesson) => {
              if (lesson.test && lesson.test.questions && lesson.test.questions.length > 0) {
                // Преобразуем тест из формата урока в формат Test для массива tests
                const test: Test = {
                  id: lesson.test.id || Date.now().toString(),
                  timeToComplete: lesson.test.timeToComplete || '10-15 минут',
                  questions: lesson.test.questions.map((q: any) => ({
                    id: q.id || Date.now().toString(),
                    type: (q.type === 'multiple' ? 'multiple' : (q.type === 'open' ? 'open' : 'single')) as 'single' | 'multiple' | 'open',
                    text: q.text || '',
                    options: (q.options || []).map((opt: any) => ({
                      id: opt.id || Date.now().toString(),
                      text: opt.text || '',
                      isCorrect: opt.isCorrect || opt.is_correct || false,
                    })),
                  })),
                };
                testsFromLessons.push(test);
              }
            });

            // Устанавливаем тесты из уроков
            if (testsFromLessons.length > 0) {
              setTests(testsFromLessons);
              setTestsCount(testsFromLessons.length);
              console.log('Загружены тесты из уроков:', testsFromLessons);
            } else {
              setTests([]);
              setTestsCount(0);
            }
          }
        } catch (error) {
          console.error('Ошибка при загрузке данных курса:', error);
          alert('Не удалось загрузить данные курса для редактирования');
        } finally {
          setLoadingCourseData(false);
        }
      }
    };

    loadCourseForEdit();
  }, [searchParams, userData?.id, userData?.name, userData?.surname]);

  // Загрузка существующих тегов из всех курсов
  useEffect(() => {
    const loadTags = async () => {
      try {
        setTagsLoading(true);
        // Загружаем все курсы для извлечения тегов
        const response = await getLessons({
          page_size: 100,
        });

        // Извлекаем все уникальные теги из курсов
        const allTagsSet = new Set<string>();
        response.results.forEach((course: ApiLesson) => {
          if (course.tags && Array.isArray(course.tags)) {
            course.tags.forEach((tag: string) => {
              // Исключаем служебные теги
              if (
                tag !== 'organizational' &&
                tag !== 'profile' &&
                !tag.startsWith('access_')
              ) {
                allTagsSet.add(tag);
              }
            });
          }
        });

        // Преобразуем в формат для react-select
        const formattedTags = Array.from(allTagsSet)
          .sort()
          .map((tag) => ({
            value: tag,
            label: tag.charAt(0).toUpperCase() + tag.slice(1),
          }));

        setAvailableTags(formattedTags);
      } catch (error) {
        console.error('Ошибка при загрузке тегов:', error);
      } finally {
        setTagsLoading(false);
      }
    };

    if (activeTab === 'training' && materialType === 'course') {
      loadTags();
    }
  }, [activeTab, materialType]);

  // Загрузка курсов (для выбора при добавлении урока или теста)
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setCoursesLoading(true);
        // Загружаем курсы из API
        const response = await getLessons({
          tags: 'organizational,profile', // Фильтруем только курсы обучения
          page_size: 100,
        });

        // Преобразуем данные API в формат для react-select
        const formattedCourses = response.results.map((course: ApiLesson) => {
          // Используем name из новой структуры API
          const title = course.name || `Курс ${course.id}`;
          
          return {
            value: course.id,
            label: title,
          };
        });
        setCourses(formattedCourses);
      } catch (error) {
        console.error('Ошибка при загрузке курсов:', error);
      } finally {
        setCoursesLoading(false);
      }
    };

    if (activeTab === 'training' && (materialType === 'lesson' || materialType === 'test')) {
      loadCourses();
    }
  }, [activeTab, materialType]);

  // Загрузка уроков курса (для выбора при добавлении теста)
  useEffect(() => {
    const loadLessons = async () => {
      if (!selectedCourseForTest) {
        setLessonsForTest([]);
        return;
      }

      try {
        setLessonsLoading(true);
        // Загружаем уроки курса из API
        const lessons = await getLessonsByKnowledgeId(selectedCourseForTest.value);

        // Преобразуем данные API в формат для react-select
        const formattedLessons = lessons.map((lesson, index) => {
          // Используем text из ApiLessonItem (урок имеет поле text)
          const textLines = lesson.text ? lesson.text.split('\n').filter((line: string) => line.trim()) : [];
          const title = textLines[0] || `Урок ${index + 1}`;
          
          return {
            value: lesson.id,
            label: title,
          };
        });
        setLessonsForTest(formattedLessons);
      } catch (error) {
        console.error('Ошибка при загрузке уроков:', error);
      } finally {
        setLessonsLoading(false);
      }
    };

    if (activeTab === 'training' && materialType === 'test' && selectedCourseForTest) {
      loadLessons();
    }
  }, [activeTab, materialType, selectedCourseForTest]);

  return (
    <div className="add-material">
      {/* Breadcrumbs */}
      <div className="add-material__breadcrumbs">
        <span>Главная</span>
        <span className="add-material__breadcrumb-separator">›</span>
        <span>Администрирование</span>
        <span className="add-material__breadcrumb-separator">›</span>
        <span>{editMode ? 'Редактирование материала' : 'Добавление материала'}</span>
      </div>

      {/* Header */}
      <div className="add-material__header">
        <button
          className="add-material__back-button"
          onClick={() => navigate('/knowledge-base/admin')}
        >
          <ArrowIcon />
        </button>
        <h1 className="add-material__title">{editMode ? 'Редактирование материала' : 'Добавление материала'}</h1>
      </div>

      {/* Form Card */}
      <div className="add-material__form-card">
        {/* Tabs */}
        <div className="add-material__tabs">
          <button
            className={`add-material__tab ${
              activeTab === 'training' ? 'add-material__tab--active' : ''
            }`}
            onClick={() => setActiveTab('training')}
          >
            Обучение
          </button>
          {/* <button
            className={`add-material__tab ${
              activeTab === 'knowledge' ? 'add-material__tab--active' : ''
            }`}
            onClick={() => setActiveTab('knowledge')}
          >
            База знаний
          </button> */}
        </div>

        {/* Form Content */}
        <div className="add-material__form-content">
          {activeTab === 'training' ? (
            <>
              {/* Training Type Selection */}
              <div className="add-material__field-group">
                <label className="add-material__label">
                  Выберите тип обучения<span className="add-material__required">*</span>
                </label>
                <div className="add-material__radio-group">
                  <label className="add-material__radio-label">
                    <input
                      type="radio"
                      name="trainingType"
                      value="organizational"
                      checked={trainingType === 'organizational'}
                      onChange={(e) => setTrainingType(e.target.value as 'organizational' | 'profile')}
                    />
                    <span>Организационное</span>
                  </label>
                  <label className="add-material__radio-label">
                    <input
                      type="radio"
                      name="trainingType"
                      value="profile"
                      checked={trainingType === 'profile'}
                      onChange={(e) => setTrainingType(e.target.value as 'organizational' | 'profile')}
                    />
                    <span>Профильное</span>
                  </label>
                </div>
              </div>

          {/* Material Type Selection */}
          <div className="add-material__field-group">
            <label className="add-material__label">
              Выберите материал для добавления<span className="add-material__required">*</span>
            </label>
            <div className="add-material__radio-group">
              <label className="add-material__radio-label">
                <input
                  type="radio"
                  name="materialType"
                  value="course"
                  checked={materialType === 'course'}
                  onChange={(e) => setMaterialType(e.target.value as 'course' | 'lesson' | 'test')}
                />
                <span>Курс</span>
              </label>
              <label className="add-material__radio-label">
                <input
                  type="radio"
                  name="materialType"
                  value="lesson"
                  checked={materialType === 'lesson'}
                  onChange={(e) => setMaterialType(e.target.value as 'course' | 'lesson' | 'test')}
                />
                <span>Урок</span>
              </label>
              <label className="add-material__radio-label">
                <input
                  type="radio"
                  name="materialType"
                  value="test"
                  checked={materialType === 'test'}
                  onChange={(e) => setMaterialType(e.target.value as 'course' | 'lesson' | 'test')}
                />
                <span>Тест</span>
              </label>
            </div>
          </div>

          {/* Course Details Section */}
          {materialType === 'course' && (
            <>
              <div className="add-material__section-label">Курс</div>
              <div className="add-material__course-section">
                {/* Left Column */}
                <div className="add-material__column">
                  <div className="add-material__field">
                    <label className="add-material__label">
                      Название курса<span className="add-material__required">*</span>
                    </label>
                    <div className="add-material__input-wrapper">
                      <input
                        type="text"
                        className="add-material__input"
                        placeholder="Введите название курса"
                        value={courseName}
                        onChange={(e) => setCourseName(e.target.value)}
                      />
                      <svg
                        className="add-material__input-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                          stroke="#2A5E38"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">Описание курса</label>
                    <div className="add-material__input-wrapper">
                      <input
                        type="text"
                        className="add-material__input"
                        placeholder="Введите описание курса"
                        value={courseDescription}
                        onChange={(e) => setCourseDescription(e.target.value)}
                      />
                      <svg
                        className="add-material__input-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                          stroke="#2A5E38"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">Обложка курса</label>
                    {/* Отображение загруженных изображений в режиме редактирования */}
                    {editMode && editCourseData && editCourseData.images && editCourseData.images.length > 0 && (
                      <div className="add-material__images-list" style={{ marginBottom: '16px' }}>
                        {editCourseData.images.map((image) => (
                          <div key={image.id} className="add-material__image-item" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            marginBottom: '8px',
                            padding: '8px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px'
                          }}>
                            <img 
                              src={image.image} 
                              alt="Обложка курса" 
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                            <button
                              type="button"
                              onClick={() => handleDeleteImage(image.id)}
                              style={{
                                padding: '4px 8px',
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Удалить
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="add-material__file-upload">
                      <input
                        type="file"
                        id="cover-upload"
                        accept="image/png,image/jpeg"
                        onChange={handleFileSelect}
                        className="add-material__file-input"
                        style={{ display: 'none' }}
                      />
                      {/* Отображение выбранного файла */}
                      {coverFile && (
                        <div style={{ 
                          marginBottom: '12px', 
                          padding: '12px', 
                          background: '#f5f5f5', 
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {coverFile.type.startsWith('image/') ? (
                              <FilePreview file={coverFile} />
                            ) : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                            <div>
                              <div style={{ fontWeight: '500', color: '#333' }}>{coverFile.name}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {(coverFile.size / 1024 / 1024).toFixed(2)} МБ
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setCoverFile(null)}
                            style={{
                              padding: '4px 8px',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Удалить
                          </button>
                        </div>
                      )}
                      {!coverFile && !(editMode && editCourseData && editCourseData.images && editCourseData.images.length > 0) && (
                        <label htmlFor="cover-upload" className="add-material__file-label" style={{ cursor: 'pointer' }}>
                          <svg
                            className="add-material__upload-icon"
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                          >
                            <path
                              d="M32 20L24 12L16 20M24 12V32M8 36H40"
                              stroke="#7A8699"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p className="add-material__upload-text">
                            Загрузите файл в формате PNG, JPEG до 5 МБ
                          </p>
                          <button 
                            type="button" 
                            className="add-material__file-button"
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById('cover-upload')?.click();
                            }}
                          >
                            Выбрать файл
                          </button>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="add-material__column">
                  <div className="add-material__field">
                    <label className="add-material__label">
                      Доступ к курсу<span className="add-material__required">*</span>
                    </label>
                    <Select
                      isMulti
                      value={accessLevel}
                      onChange={(selectedOptions) => {
                        const newOptions = selectedOptions as Array<{ value: number | string; label: string }> || [];
                        
                        // Проверяем, выбрана ли опция "Все"
                        const allSelected = newOptions.some(opt => opt.value === ALL_USERS_VALUE);
                        
                        if (allSelected) {
                          // Если выбрано "Все", устанавливаем всех пользователей
                          const allUsersOptions = users.map(user => ({
                            value: user.value,
                            label: user.label
                          }));
                          setAccessLevel(allUsersOptions);
                          return;
                        }
                        
                        // Проверяем, что автор не был удален
                        if (userData?.id) {
                          const authorId = userData.id;
                          const authorStillExists = newOptions.some(opt => opt.value === authorId);
                          if (!authorStillExists) {
                            // Если автора удалили, возвращаем его обратно
                            const authorOption = accessLevel.find(opt => opt.value === authorId);
                            if (authorOption) {
                              setAccessLevel([authorOption, ...newOptions]);
                              return;
                            }
                          }
                        }
                        setAccessLevel(newOptions);
                      }}
                      options={[
                        { value: ALL_USERS_VALUE, label: 'Все' },
                        ...users
                      ]}
                      isLoading={usersLoading}
                      placeholder="Выберите кому доступен курс"
                      noOptionsMessage={() => "Нет доступных пользователей"}
                      menuPortalTarget={document.body}
                      components={{
                        MultiValueRemove: (props: any) => {
                          // Скрываем кнопку удаления для автора
                          if (userData?.id && props.data.value === userData.id) {
                            return null;
                          }
                          return <components.MultiValueRemove {...props} />;
                        }
                      }}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: state.isFocused ? '1px solid #2A5E38' : '1px solid #e0e0e0',
                          borderRadius: '8px',
                          minHeight: '44px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#2A5E38',
                          },
                        }),
                        menu: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          zIndex: 9999,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#2A5E38'
                            : state.isFocused
                            ? '#f0f9f4'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#2A5E38' : '#f0f9f4',
                          },
                        }),
                        multiValue: (provided) => ({
                          ...provided,
                          backgroundColor: '#E8E8F8',
                          borderRadius: '6px',
                        }),
                        multiValueLabel: (provided) => ({
                          ...provided,
                          color: '#333',
                          fontSize: '14px',
                          padding: '4px 8px',
                        }),
                        multiValueRemove: (provided) => ({
                          ...provided,
                          borderRadius: '0 6px 6px 0',
                          '&:hover': {
                            backgroundColor: '#2A5E38',
                            color: 'white',
                          },
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#999',
                          fontSize: '14px',
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#7A8699',
                          '&:hover': {
                            color: '#333',
                          },
                        }),
                      }}
                      className="add-material__multi-select"
                    />
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">Цель курса</label>
                    <div className="add-material__input-wrapper">
                      <input
                        type="text"
                        className="add-material__input"
                        placeholder="Введите цель курса"
                        value={courseGoal}
                        onChange={(e) => setCourseGoal(e.target.value)}
                      />
                      <svg
                        className="add-material__input-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                          stroke="#2A5E38"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">Теги</label>
                    <CreatableSelect
                      isMulti
                      isClearable
                      isLoading={tagsLoading}
                      value={courseTags}
                      onChange={(newValue) => {
                        setCourseTags((newValue as Array<{ value: string; label: string }>) || []);
                      }}
                      options={availableTags}
                      placeholder="Выберите теги или введите новый"
                      formatCreateLabel={(inputValue) => `Создать "${inputValue}"`}
                      noOptionsMessage={() => 'Начните вводить для создания нового тега'}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: state.isFocused ? '#2A5E38' : '#E0E0E0',
                          boxShadow: state.isFocused ? '0 0 0 1px #2A5E38' : 'none',
                          '&:hover': {
                            borderColor: state.isFocused ? '#2A5E38' : '#B0B0B0',
                          },
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#2A5E38'
                            : state.isFocused
                            ? '#f0f9f4'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#2A5E38' : '#f0f9f4',
                          },
                        }),
                        multiValue: (provided) => ({
                          ...provided,
                          backgroundColor: '#2A5E38',
                        }),
                        multiValueLabel: (provided) => ({
                          ...provided,
                          color: 'white',
                        }),
                        multiValueRemove: (provided) => ({
                          ...provided,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#1a4a2a',
                            color: 'white',
                          },
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#999',
                        }),
                      }}
                    />
                    <p className="add-material__hint">
                      Выберите существующие теги или создайте новые. Теги используются для категоризации курсов и будут отображаться в навигации.
                    </p>
                  </div>
                </div>
              </div>

              {/* Lessons Section */}
              <div className="add-material__content-section add-material__content-section--lessons">
                <div className="add-material__content-header">
                  <h3 className="add-material__content-title">Уроки ({lessonsCount})</h3>
                </div>

                {/* Lesson Forms */}
                {lessons.map((lesson, index) => (
                  <div key={lesson.id} className="add-material__lesson-form">
                    <div className="add-material__lesson-header">
                      <h4 className="add-material__lesson-title">Урок {index + 1}</h4>
                      <button
                        type="button"
                        className="add-material__remove-button"
                        onClick={() => handleRemoveLesson(lesson.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>

                    {/* Lesson Form Fields */}
                    <div className="add-material__course-section">
                      {/* Left Column */}
                      <div className="add-material__column">
                        <div className="add-material__field">
                          <label className="add-material__label">
                            Название урока<span className="add-material__required">*</span>
                          </label>
                          <div className="add-material__input-wrapper">
                            <input
                              type="text"
                              className="add-material__input"
                              placeholder="Введите название урока"
                              value={lesson.lessonName}
                              onChange={(e) => handleLessonChange(lesson.id, 'lessonName', e.target.value)}
                            />
                            <svg
                              className="add-material__input-icon"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                                stroke="#2A5E38"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        <div className="add-material__field">
                          <label className="add-material__label">
                            Сложность<span className="add-material__required">*</span>
                          </label>
                          <div className="add-material__select-wrapper">
                            <select
                              className="add-material__select"
                              value={lesson.difficulty}
                              onChange={(e) => handleLessonChange(lesson.id, 'difficulty', e.target.value)}
                            >
                              <option value="">Выберите сложность урока</option>
                              <option value="easy">Легкая</option>
                              <option value="medium">Средняя</option>
                              <option value="hard">Сложная</option>
                            </select>
                            <svg
                              className="add-material__select-arrow"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M4 6L8 10L12 6"
                                stroke="#7A8699"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="add-material__column">
                        <div className="add-material__field">
                          <label className="add-material__label">
                            Продолжительность урока<span className="add-material__required">*</span>
                          </label>
                          <div className="add-material__select-wrapper">
                            <select
                              className="add-material__select"
                              value={lesson.duration || ''}
                              onChange={(e) => handleLessonChange(lesson.id, 'duration', e.target.value)}
                            >
                              <option value="">Выберите продолжительность</option>
                              <option value="1 мин">1 минута</option>
                              <option value="2 мин">2 минуты</option>
                              <option value="3 мин">3 минуты</option>
                              <option value="5 мин">5 минут</option>
                              <option value="10 мин">10 минут</option>
                              <option value="15 мин">15 минут</option>
                              <option value="20 мин">20 минут</option>
                              <option value="30 мин">30 минут</option>
                              <option value="45 мин">45 минут</option>
                              <option value="60 мин">1 час</option>
                              <option value="90 мин">1.5 часа</option>
                              <option value="120 мин">2 часа</option>
                              <option value="180 мин">3 часа</option>
                            </select>
                            <svg
                              className="add-material__select-arrow"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M4 6L8 10L12 6"
                                stroke="#7A8699"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* Информация о тесте урока в режиме редактирования */}
                        {editMode && lesson.test && (
                          <div className="add-material__field" style={{ 
                            padding: '12px', 
                            background: '#f5f5f5', 
                            borderRadius: '8px',
                            marginBottom: '16px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <label className="add-material__label" style={{ marginBottom: '8px' }}>
                                  Тест урока
                                </label>
                                <div style={{ fontSize: '14px', color: '#666' }}>
                                  Вопросов: {lesson.test.questions?.length || 0} | 
                                  Время: {lesson.test.timeToComplete || '10-15 минут'}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDeleteLessonTest(lesson.id)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Удалить тест
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Blocks */}
                    {lesson.blocks.map((block, blockIndex) => (
                      <div key={block.id} className="add-material__block">
                        <div className="add-material__block-header">
                          <h5 className="add-material__block-title">Блок {blockIndex + 1}</h5>
                          <button
                            type="button"
                            className="add-material__remove-button"
                            onClick={() => handleRemoveBlock(lesson.id, block.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>

                        {/* Content Type Selection for Block */}
                        <div className="add-material__field-group">
                          <label className="add-material__label">
                            Выберите тип контента блока<span className="add-material__required">*</span>
                          </label>
                          <div className="add-material__radio-group">
                            <label className="add-material__radio-label">
                              <input
                                type="radio"
                                name={`blockType-${lesson.id}-${block.id}`}
                                value="video"
                                checked={block.type === 'video'}
                                onChange={(e) => handleBlockChange(lesson.id, block.id, 'type', e.target.value)}
                              />
                              <span>Видео</span>
                            </label>
                            <label className="add-material__radio-label">
                              <input
                                type="radio"
                                name={`blockType-${lesson.id}-${block.id}`}
                                value="file"
                                checked={block.type === 'file'}
                                onChange={(e) => handleBlockChange(lesson.id, block.id, 'type', e.target.value)}
                              />
                              <span>Файл</span>
                            </label>
                            <label className="add-material__radio-label">
                              <input
                                type="radio"
                                name={`blockType-${lesson.id}-${block.id}`}
                                value="text-media"
                                checked={block.type === 'text-media'}
                                onChange={(e) => handleBlockChange(lesson.id, block.id, 'type', e.target.value)}
                              />
                              <span>Текст+медиа</span>
                            </label>
                          </div>
                        </div>

                        <div className="add-material__course-section">
                          {/* Left Column - специфичные поля для типа контента */}
                          <div className="add-material__column">
                            {block.type === 'video' && (
                              <div className="add-material__field">
                                <label className="add-material__label">
                                  Ссылка на видео
                                </label>
                                <div className="add-material__input-wrapper">
                                  <input
                                    type="text"
                                    className="add-material__input"
                                    placeholder="Введите ссылку на видео"
                                    value={block.videoLink || ''}
                                    onChange={(e) => handleBlockChange(lesson.id, block.id, 'videoLink', e.target.value)}
                                  />
                                  <svg
                                    className="add-material__input-icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                  >
                                    <path
                                      d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                                      stroke="#2A5E38"
                                      strokeWidth="1.25"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}

                            {block.type === 'file' && (
                              <div className="add-material__field">
                                <label className="add-material__label">
                                  Файл урока
                                </label>
                                <div className="add-material__file-upload">
                                  <input
                                    type="file"
                                    id={`lesson-file-${lesson.id}-${block.id}`}
                                    accept=".pdf,.xlsx,.png,.jpeg,.jpg"
                                    onChange={(e) => handleBlockFileSelect(lesson.id, block.id, 'lessonFile', e)}
                                    className="add-material__file-input"
                                    style={{ display: 'none' }}
                                  />
                                  {/* Отображение выбранного файла */}
                                  {(block.lessonFile || block.lessonFileUrl) && (
                                    <div style={{ 
                                      marginBottom: '12px', 
                                      padding: '12px', 
                                      background: '#f5f5f5', 
                                      borderRadius: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {block.lessonFile instanceof File && block.lessonFile.type.startsWith('image/') ? (
                                          <FilePreview file={block.lessonFile} />
                                        ) : block.lessonFileUrl && (block.lessonFileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                          <img 
                                            src={block.lessonFileUrl} 
                                            alt="Preview" 
                                            style={{
                                              width: '60px',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px',
                                              border: '1px solid #e0e0e0'
                                            }}
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        ))}
                                        <div>
                                          <div style={{ fontWeight: '500', color: '#333' }}>
                                            {block.lessonFile instanceof File ? block.lessonFile.name : (block.lessonFileUrl ? block.lessonFileUrl.split('/').pop() : 'Файл выбран')}
                                          </div>
                                          {block.lessonFile instanceof File && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                              {(block.lessonFile.size / 1024 / 1024).toFixed(2)} МБ
                                            </div>
                                          )}
                                          {block.lessonFileUrl && !block.lessonFile && (
                                            <a 
                                              href={block.lessonFileUrl} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              style={{ fontSize: '12px', color: '#2A5E38', textDecoration: 'underline' }}
                                            >
                                              Открыть файл
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleBlockChange(lesson.id, block.id, 'lessonFile', null);
                                          handleBlockChange(lesson.id, block.id, 'lessonFileUrl', undefined);
                                        }}
                                        style={{
                                          padding: '4px 8px',
                                          background: '#e74c3c',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '12px'
                                        }}
                                      >
                                        Удалить
                                      </button>
                                    </div>
                                  )}
                                  {!(block.lessonFile || block.lessonFileUrl) && (
                                    <label htmlFor={`lesson-file-${lesson.id}-${block.id}`} className="add-material__file-label" style={{ cursor: 'pointer' }}>
                                      <svg
                                        className="add-material__upload-icon"
                                        width="48"
                                        height="48"
                                        viewBox="0 0 48 48"
                                        fill="none"
                                      >
                                        <path
                                          d="M32 20L24 12L16 20M24 12V32M8 36H40"
                                          stroke="#7A8699"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      <p className="add-material__upload-text">
                                        Загрузите файл в формате PDF, XLSX, PNG, JPEG до 50 МБ
                                      </p>
                                      <button 
                                        type="button" 
                                        className="add-material__file-button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          document.getElementById(`lesson-file-${lesson.id}-${block.id}`)?.click();
                                        }}
                                      >
                                        Выбрать файл
                                      </button>
                                    </label>
                                  )}
                                </div>
                              </div>
                            )}

                            {block.type === 'text-media' && (
                              <div className="add-material__field">
                                <label className="add-material__label">
                                  Загрузка медиа
                                </label>
                                <div className="add-material__file-upload">
                                  <input
                                    type="file"
                                    id={`media-file-${lesson.id}-${block.id}`}
                                    accept=".pdf,.xlsx,.png,.jpeg,.jpg,.mp4,.mov"
                                    onChange={(e) => handleBlockFileSelect(lesson.id, block.id, 'mediaFile', e)}
                                    className="add-material__file-input"
                                    style={{ display: 'none' }}
                                  />
                                  {/* Отображение выбранного файла */}
                                  {(block.mediaFile || block.mediaFileUrl) && (
                                    <div style={{ 
                                      marginBottom: '12px', 
                                      padding: '12px', 
                                      background: '#f5f5f5', 
                                      borderRadius: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {block.mediaFile instanceof File && block.mediaFile.type.startsWith('image/') ? (
                                          <FilePreview file={block.mediaFile} />
                                        ) : block.mediaFileUrl && (block.mediaFileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                          <img 
                                            src={block.mediaFileUrl} 
                                            alt="Preview" 
                                            style={{
                                              width: '60px',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px',
                                              border: '1px solid #e0e0e0'
                                            }}
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        ))}
                                        <div>
                                          <div style={{ fontWeight: '500', color: '#333' }}>
                                            {block.mediaFile instanceof File ? block.mediaFile.name : (block.mediaFileUrl ? block.mediaFileUrl.split('/').pop() : 'Файл выбран')}
                                          </div>
                                          {block.mediaFile instanceof File && (
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                              {(block.mediaFile.size / 1024 / 1024).toFixed(2)} МБ
                                            </div>
                                          )}
                                          {block.mediaFileUrl && !block.mediaFile && (
                                            <a 
                                              href={block.mediaFileUrl} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              style={{ fontSize: '12px', color: '#2A5E38', textDecoration: 'underline' }}
                                            >
                                              Открыть файл
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleBlockChange(lesson.id, block.id, 'mediaFile', null);
                                          handleBlockChange(lesson.id, block.id, 'mediaFileUrl', undefined);
                                        }}
                                        style={{
                                          padding: '4px 8px',
                                          background: '#e74c3c',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '12px'
                                        }}
                                      >
                                        Удалить
                                      </button>
                                    </div>
                                  )}
                                  {!(block.mediaFile || block.mediaFileUrl) && (
                                    <label htmlFor={`media-file-${lesson.id}-${block.id}`} className="add-material__file-label" style={{ cursor: 'pointer' }}>
                                      <svg
                                        className="add-material__upload-icon"
                                        width="48"
                                        height="48"
                                        viewBox="0 0 48 48"
                                        fill="none"
                                      >
                                        <path
                                          d="M32 20L24 12L16 20M24 12V32M8 36H40"
                                          stroke="#7A8699"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      <p className="add-material__upload-text">
                                        Загрузите медиа файл
                                      </p>
                                      <button 
                                        type="button" 
                                        className="add-material__file-button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          document.getElementById(`media-file-${lesson.id}-${block.id}`)?.click();
                                        }}
                                      >
                                        Выбрать файл
                                      </button>
                                    </label>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right Column - текстовое поле */}
                          <div className="add-material__column">
                            <div className="add-material__field">
                              <label className="add-material__label">
                                Текст блока (опционально)
                              </label>
                              <textarea
                                className="add-material__textarea"
                                placeholder="Введите текст блока"
                                value={block.text || ''}
                                onChange={(e) => handleBlockChange(lesson.id, block.id, 'text', e.target.value)}
                                rows={6}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Block Button - moved to the end */}
                    <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        className="add-material__add-block-button"
                        onClick={() => handleAddBlock(lesson.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        Добавить блок
                      </button>
                    </div>

                    {/* Показываем поле загрузки материала только если нет блоков или не все блоки типа 'text-media' */}
                    {(lesson.blocks.length === 0 || !lesson.blocks.every(block => block.type === 'text-media')) && (
                      <div className="add-material__field" style={{ marginTop: '24px' }}>
                        <label className="add-material__label">
                          Материал для скачивания пользователями (опционально)
                        </label>
                        <div className="add-material__file-upload">
                          <input
                            type="file"
                            id={`download-material-${lesson.id}`}
                            accept=".pdf,.xlsx,.png,.jpeg,.jpg"
                            onChange={(e) => handleLessonFileSelect(lesson.id, e)}
                            className="add-material__file-input"
                            style={{ display: 'none' }}
                          />
                          {/* Отображение выбранного файла */}
                          {(lesson.downloadMaterial || lesson.downloadMaterialUrl) && (
                            <div style={{ 
                              marginBottom: '12px', 
                              padding: '12px', 
                              background: '#f5f5f5', 
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {lesson.downloadMaterial instanceof File && lesson.downloadMaterial.type.startsWith('image/') ? (
                                  <FilePreview file={lesson.downloadMaterial} />
                                ) : lesson.downloadMaterialUrl && (lesson.downloadMaterialUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <img 
                                    src={lesson.downloadMaterialUrl} 
                                    alt="Preview" 
                                    style={{
                                      width: '60px',
                                      height: '60px',
                                      objectFit: 'cover',
                                      borderRadius: '6px',
                                      border: '1px solid #e0e0e0'
                                    }}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                ))}
                                <div>
                                  <div style={{ fontWeight: '500', color: '#333' }}>
                                    {lesson.downloadMaterial instanceof File ? lesson.downloadMaterial.name : (lesson.downloadMaterialUrl ? lesson.downloadMaterialUrl.split('/').pop() : 'Файл выбран')}
                                  </div>
                                  {lesson.downloadMaterial instanceof File && (
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                      {(lesson.downloadMaterial.size / 1024 / 1024).toFixed(2)} МБ
                                    </div>
                                  )}
                                  {lesson.downloadMaterialUrl && !lesson.downloadMaterial && (
                                    <a 
                                      href={lesson.downloadMaterialUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ fontSize: '12px', color: '#2A5E38', textDecoration: 'underline' }}
                                    >
                                      Открыть файл
                                    </a>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  handleLessonChange(lesson.id, 'downloadMaterial', null);
                                  handleLessonChange(lesson.id, 'downloadMaterialUrl', undefined);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  background: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Удалить
                              </button>
                            </div>
                          )}
                          {!(lesson.downloadMaterial || lesson.downloadMaterialUrl) && (
                            <label htmlFor={`download-material-${lesson.id}`} className="add-material__file-label" style={{ cursor: 'pointer' }}>
                              <svg
                                className="add-material__upload-icon"
                                width="48"
                                height="48"
                                viewBox="0 0 48 48"
                                fill="none"
                              >
                                <path
                                  d="M32 20L24 12L16 20M24 12V32M8 36H40"
                                  stroke="#7A8699"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <p className="add-material__upload-text">
                                Загрузите файл в формате PDF, XLSX, PNG, JPEG до 50 МБ
                              </p>
                              <button 
                                type="button" 
                                className="add-material__file-button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  document.getElementById(`download-material-${lesson.id}`)?.click();
                                }}
                              >
                                Выбрать файл
                              </button>
                            </label>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add Lesson Button - moved to the end */}
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-start' }}>
                  <button
                    type="button"
                    className="add-material__add-button add-material__add-button--lessons"
                    onClick={handleAddLesson}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Добавить урок
                  </button>
                </div>
              </div>

              {/* Tests Section */}
              <div className="add-material__content-section add-material__content-section--tests">
                <div className="add-material__content-header">
                  <h3 className="add-material__content-title">
                    Тест после прохождения курса ({testsCount})
                  </h3>
                  <button
                    type="button"
                    className="add-material__add-button add-material__add-button--tests"
                    onClick={handleAddTest}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    Добавить тест
                  </button>
                </div>

                {/* Test Forms */}
                {tests.map((test, testIndex) => (
                  <div key={test.id} className="add-material__test-form">
                    <div className="add-material__test-header">
                      <h4 className="add-material__test-title">Тест {testIndex + 1}</h4>
                      <button
                        type="button"
                        className="add-material__remove-button"
                        onClick={() => handleRemoveTest(test.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>

                    {/* Test Info */}
                    <div className="add-material__test-info">
                      <div className="add-material__test-info-item">
                        <span className="add-material__test-info-label">Количество вопросов</span>
                        <span className="add-material__test-info-value">{test.questions.length}</span>
                      </div>
                      <div className="add-material__test-info-item">
                        <span className="add-material__test-info-label">Время для прохождения</span>
                        <div className="add-material__select-wrapper" style={{ minWidth: '200px' }}>
                          <select
                            className="add-material__select"
                            value={test.timeToComplete || ''}
                            onChange={(e) => handleTestChange(test.id, 'timeToComplete', e.target.value)}
                          >
                            <option value="">Выберите время</option>
                            <option value="1 мин">1 минута</option>
                            <option value="2 мин">2 минуты</option>
                            <option value="3 мин">3 минуты</option>
                            <option value="5 мин">5 минут</option>
                            <option value="10 мин">10 минут</option>
                            <option value="15 мин">15 минут</option>
                            <option value="20 мин">20 минут</option>
                            <option value="25 мин">25 минут</option>
                            <option value="30 мин">30 минут</option>
                          </select>
                          <svg
                            className="add-material__select-arrow"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M4 6L8 10L12 6"
                              stroke="#7A8699"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Questions */}
                    {test.questions.map((question, questionIndex) => (
                      <div key={question.id} className="add-material__question-form">
                        <div className="add-material__question-header">
                          <h5 className="add-material__question-title">Вопрос {questionIndex + 1}</h5>
                          <button
                            type="button"
                            className="add-material__remove-button"
                            onClick={() => handleRemoveQuestion(test.id, question.id)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>

                        {/* Question Type Selection */}
                        <div className="add-material__field-group">
                          <label className="add-material__label">
                            Выберите тип вопроса<span className="add-material__required">*</span>
                          </label>
                          <div className="add-material__radio-group">
                            <label className="add-material__radio-label">
                              <input
                                type="radio"
                                name={`question-type-${test.id}-${question.id}`}
                                value="single"
                                checked={question.type === 'single'}
                                onChange={(e) => handleQuestionChange(test.id, question.id, 'type', e.target.value)}
                              />
                              <span>Один правильный ответ</span>
                            </label>
                            <label className="add-material__radio-label">
                              <input
                                type="radio"
                                name={`question-type-${test.id}-${question.id}`}
                                value="multiple"
                                checked={question.type === 'multiple'}
                                onChange={(e) => handleQuestionChange(test.id, question.id, 'type', e.target.value)}
                              />
                              <span>Несколько правильных ответов</span>
                            </label>
                            {/* Вопрос со свободным ответом временно отключён — бэкенд ещё не проработал логику
                            <label className="add-material__radio-label">
                              <input
                                type="radio"
                                name={`question-type-${test.id}-${question.id}`}
                                value="open"
                                checked={question.type === 'open'}
                                onChange={(e) => handleQuestionChange(test.id, question.id, 'type', e.target.value)}
                              />
                              <span>Открытый вопрос</span>
                            </label>
                            */}
                          </div>
                        </div>

                        {/* Question Text */}
                        <div className="add-material__field">
                          <label className="add-material__label">
                            Текст вопроса<span className="add-material__required">*</span>
                          </label>
                          <div className="add-material__input-wrapper">
                            <input
                              type="text"
                              className="add-material__input"
                              placeholder="Введите текст вопроса"
                              value={question.text}
                              onChange={(e) => handleQuestionChange(test.id, question.id, 'text', e.target.value)}
                            />
                            <svg
                              className="add-material__input-icon"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                                stroke="#2A5E38"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* Answer Options - только для типов single и multiple */}
                        {(question.type === 'single' || question.type === 'multiple') && (
                          <div className="add-material__options-section">
                            {question.options.map((option, optionIndex) => (
                              <div key={option.id} className="add-material__option-row">
                                <div className="add-material__option-control">
                                  {question.type === 'single' ? (
                                    <input
                                      type="radio"
                                      name={`correct-answer-${test.id}-${question.id}`}
                                      checked={option.isCorrect}
                                      onChange={() => handleCorrectAnswerChange(test.id, question.id, option.id, true)}
                                      className="add-material__option-radio"
                                    />
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={option.isCorrect}
                                      onChange={(e) => handleCorrectAnswerChange(test.id, question.id, option.id, e.target.checked)}
                                      className="add-material__option-checkbox"
                                    />
                                  )}
                                </div>
                                <div className="add-material__option-input-wrapper">
                                  <input
                                    type="text"
                                    className="add-material__option-input"
                                    placeholder="Введите вариант ответа"
                                    value={option.text}
                                    onChange={(e) => handleOptionChange(test.id, question.id, option.id, 'text', e.target.value)}
                                  />
                                  <svg
                                    className="add-material__input-icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                  >
                                    <path
                                      d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                                      stroke="#2A5E38"
                                      strokeWidth="1.25"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                                <button
                                  type="button"
                                  className="add-material__remove-option-button"
                                  onClick={() => handleRemoveOption(test.id, question.id, option.id)}
                                  title="Удалить вариант ответа"
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="add-material__add-option-button"
                              onClick={() => handleAddAnswerOption(test.id, question.id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add Question Button */}
                    <button
                      type="button"
                      className="add-material__add-question-button"
                      onClick={() => handleAddQuestion(test.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      Добавить вопрос
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

              {/* Standalone Lesson Form */}
              {materialType === 'lesson' && (
                <>
                  <div className="add-material__section-label">Урок</div>
                  
                  {/* Course Selection for Lesson */}
                  <div className="add-material__field">
                    <label className="add-material__label">
                      Выберите курс<span className="add-material__required">*</span>
                    </label>
                    <Select
                      value={selectedCourseForLesson}
                      onChange={(selectedOption) => {
                        setSelectedCourseForLesson(selectedOption as { value: number; label: string } | null);
                        setStandaloneLesson(null); // Сбрасываем урок при смене курса
                      }}
                      options={courses}
                      isLoading={coursesLoading}
                      placeholder="Выберите курс для добавления урока"
                      noOptionsMessage={() => "Нет доступных курсов"}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: state.isFocused ? '1px solid #2A5E38' : '1px solid #e0e0e0',
                          borderRadius: '8px',
                          minHeight: '44px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#2A5E38',
                          },
                        }),
                        menu: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          zIndex: 9999,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#2A5E38'
                            : state.isFocused
                            ? '#f0f9f4'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#2A5E38' : '#f0f9f4',
                          },
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#999',
                          fontSize: '14px',
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#7A8699',
                          '&:hover': {
                            color: '#333',
                          },
                        }),
                      }}
                      className="add-material__multi-select"
                    />
                  </div>

                  {selectedCourseForLesson && standaloneLesson ? (
                    <div className="add-material__lesson-form">

                      {/* Lesson Form Fields */}
                      <div className="add-material__course-section">
                        <div className="add-material__column">
                          <div className="add-material__field">
                            <label className="add-material__label">
                              Название урока<span className="add-material__required">*</span>
                            </label>
                            <div className="add-material__input-wrapper">
                              <input
                                type="text"
                                className="add-material__input"
                                placeholder="Введите название урока"
                                value={standaloneLesson.lessonName}
                                onChange={(e) => handleStandaloneLessonChange('lessonName', e.target.value)}
                              />
                              <svg className="add-material__input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z" stroke="#2A5E38" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>

                          <div className="add-material__field">
                            <label className="add-material__label">
                              Сложность<span className="add-material__required">*</span>
                            </label>
                            <div className="add-material__select-wrapper">
                              <select
                                className="add-material__select"
                                value={standaloneLesson.difficulty}
                                onChange={(e) => handleStandaloneLessonChange('difficulty', e.target.value)}
                              >
                                <option value="">Выберите сложность урока</option>
                                <option value="easy">Легкая</option>
                                <option value="medium">Средняя</option>
                                <option value="hard">Сложная</option>
                              </select>
                              <svg className="add-material__select-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M4 6L8 10L12 6" stroke="#7A8699" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="add-material__column">
                          <div className="add-material__field">
                            <label className="add-material__label">
                              Продолжительность урока<span className="add-material__required">*</span>
                            </label>
                            <div className="add-material__select-wrapper">
                              <select
                                className="add-material__select"
                                value={standaloneLesson.duration || ''}
                                onChange={(e) => handleStandaloneLessonChange('duration', e.target.value)}
                              >
                                <option value="">Выберите продолжительность</option>
                                <option value="1 мин">1 минута</option>
                                <option value="2 мин">2 минуты</option>
                                <option value="3 мин">3 минуты</option>
                                <option value="5 мин">5 минут</option>
                                <option value="10 мин">10 минут</option>
                                <option value="15 мин">15 минут</option>
                                <option value="20 мин">20 минут</option>
                                <option value="30 мин">30 минут</option>
                                <option value="45 мин">45 минут</option>
                                <option value="60 мин">1 час</option>
                                <option value="90 мин">1.5 часа</option>
                                <option value="120 мин">2 часа</option>
                                <option value="180 мин">3 часа</option>
                              </select>
                              <svg
                                className="add-material__select-arrow"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M4 6L8 10L12 6"
                                  stroke="#7A8699"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Blocks */}
                      {standaloneLesson.blocks.map((block, blockIndex) => (
                        <div key={block.id} className="add-material__block">
                          <div className="add-material__block-header">
                            <h5 className="add-material__block-title">Блок {blockIndex + 1}</h5>
                            <button
                              type="button"
                              className="add-material__remove-button"
                              onClick={() => handleRemoveStandaloneLessonBlock(block.id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>

                          {/* Content Type Selection for Block */}
                          <div className="add-material__field-group">
                            <label className="add-material__label">
                              Выберите тип контента блока<span className="add-material__required">*</span>
                            </label>
                            <div className="add-material__radio-group">
                              <label className="add-material__radio-label">
                                <input
                                  type="radio"
                                  name={`standalone-blockType-${block.id}`}
                                  value="video"
                                  checked={block.type === 'video'}
                                  onChange={(e) => handleStandaloneLessonBlockChange(block.id, 'type', e.target.value)}
                                />
                                <span>Видео</span>
                              </label>
                              <label className="add-material__radio-label">
                                <input
                                  type="radio"
                                  name={`standalone-blockType-${block.id}`}
                                  value="file"
                                  checked={block.type === 'file'}
                                  onChange={(e) => handleStandaloneLessonBlockChange(block.id, 'type', e.target.value)}
                                />
                                <span>Файл</span>
                              </label>
                              <label className="add-material__radio-label">
                                <input
                                  type="radio"
                                  name={`standalone-blockType-${block.id}`}
                                  value="text-media"
                                  checked={block.type === 'text-media'}
                                  onChange={(e) => handleStandaloneLessonBlockChange(block.id, 'type', e.target.value)}
                                />
                                <span>Текст+медиа</span>
                              </label>
                            </div>
                          </div>

                          <div className="add-material__course-section">
                            <div className="add-material__column">
                              {block.type === 'video' && (
                                <div className="add-material__field">
                                  <label className="add-material__label">Ссылка на видео</label>
                                  <div className="add-material__input-wrapper">
                                    <input
                                      type="text"
                                      className="add-material__input"
                                      placeholder="Введите ссылку на видео"
                                      value={block.videoLink || ''}
                                      onChange={(e) => handleStandaloneLessonBlockChange(block.id, 'videoLink', e.target.value)}
                                    />
                                    <svg className="add-material__input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z" stroke="#2A5E38" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                </div>
                              )}

                              {block.type === 'file' && (
                                <div className="add-material__field">
                                  <label className="add-material__label">Файл урока</label>
                                  <div className="add-material__file-upload">
                                    <input
                                      type="file"
                                      id={`standalone-lesson-file-${block.id}`}
                                      accept=".pdf,.xlsx,.png,.jpeg,.jpg"
                                      onChange={(e) => handleStandaloneLessonBlockFileSelect(block.id, 'lessonFile', e)}
                                      className="add-material__file-input"
                                      style={{ display: 'none' }}
                                    />
                                    {/* Отображение выбранного файла */}
                                    {(block.lessonFile || block.lessonFileUrl) && (
                                      <div style={{ 
                                        marginBottom: '12px', 
                                        padding: '12px', 
                                        background: '#f5f5f5', 
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                          {block.lessonFile instanceof File && block.lessonFile.type.startsWith('image/') ? (
                                            <FilePreview file={block.lessonFile} />
                                          ) : block.lessonFileUrl && (block.lessonFileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                            <img 
                                              src={block.lessonFileUrl} 
                                              alt="Preview" 
                                              style={{
                                                width: '60px',
                                                height: '60px',
                                                objectFit: 'cover',
                                                borderRadius: '6px',
                                                border: '1px solid #e0e0e0'
                                              }}
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                          ))}
                                          <div>
                                            <div style={{ fontWeight: '500', color: '#333' }}>
                                              {block.lessonFile instanceof File ? block.lessonFile.name : (block.lessonFileUrl ? block.lessonFileUrl.split('/').pop() : 'Файл выбран')}
                                            </div>
                                            {block.lessonFile instanceof File && (
                                              <div style={{ fontSize: '12px', color: '#666' }}>
                                                {(block.lessonFile.size / 1024 / 1024).toFixed(2)} МБ
                                              </div>
                                            )}
                                            {block.lessonFileUrl && !block.lessonFile && (
                                              <a 
                                                href={block.lessonFileUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ fontSize: '12px', color: '#2A5E38', textDecoration: 'underline' }}
                                              >
                                                Открыть файл
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleStandaloneLessonBlockChange(block.id, 'lessonFile', null);
                                            handleStandaloneLessonBlockChange(block.id, 'lessonFileUrl', undefined);
                                          }}
                                          style={{
                                            padding: '4px 8px',
                                            background: '#e74c3c',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                        >
                                          Удалить
                                        </button>
                                      </div>
                                    )}
                                    {!(block.lessonFile || block.lessonFileUrl) && (
                                      <label htmlFor={`standalone-lesson-file-${block.id}`} className="add-material__file-label" style={{ cursor: 'pointer' }}>
                                        <svg className="add-material__upload-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                          <path d="M32 20L24 12L16 20M24 12V32M8 36H40" stroke="#7A8699" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <p className="add-material__upload-text">
                                          Загрузите файл в формате PDF, XLSX, PNG, JPEG до 50 МБ
                                        </p>
                                        <button 
                                          type="button" 
                                          className="add-material__file-button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById(`standalone-lesson-file-${block.id}`)?.click();
                                          }}
                                        >
                                          Выбрать файл
                                        </button>
                                      </label>
                                    )}
                                  </div>
                                </div>
                              )}

                              {block.type === 'text-media' && (
                                <div className="add-material__field">
                                  <label className="add-material__label">Загрузка медиа</label>
                                  <div className="add-material__file-upload">
                                    <input
                                      type="file"
                                      id={`standalone-media-file-${block.id}`}
                                      accept=".pdf,.xlsx,.png,.jpeg,.jpg,.mp4,.mov"
                                      onChange={(e) => handleStandaloneLessonBlockFileSelect(block.id, 'mediaFile', e)}
                                      className="add-material__file-input"
                                      style={{ display: 'none' }}
                                    />
                                    {/* Отображение выбранного файла */}
                                    {(block.mediaFile || block.mediaFileUrl) && (
                                      <div style={{ 
                                        marginBottom: '12px', 
                                        padding: '12px', 
                                        background: '#f5f5f5', 
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                          {block.mediaFile instanceof File && block.mediaFile.type.startsWith('image/') ? (
                                            <FilePreview file={block.mediaFile} />
                                          ) : block.mediaFileUrl && (block.mediaFileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                            <img 
                                              src={block.mediaFileUrl} 
                                              alt="Preview" 
                                              style={{
                                                width: '60px',
                                                height: '60px',
                                                objectFit: 'cover',
                                                borderRadius: '6px',
                                                border: '1px solid #e0e0e0'
                                              }}
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                          ))}
                                          <div>
                                            <div style={{ fontWeight: '500', color: '#333' }}>
                                              {block.mediaFile instanceof File ? block.mediaFile.name : (block.mediaFileUrl ? block.mediaFileUrl.split('/').pop() : 'Файл выбран')}
                                            </div>
                                            {block.mediaFile instanceof File && (
                                              <div style={{ fontSize: '12px', color: '#666' }}>
                                                {(block.mediaFile.size / 1024 / 1024).toFixed(2)} МБ
                                              </div>
                                            )}
                                            {block.mediaFileUrl && !block.mediaFile && (
                                              <a 
                                                href={block.mediaFileUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ fontSize: '12px', color: '#2A5E38', textDecoration: 'underline' }}
                                              >
                                                Открыть файл
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleStandaloneLessonBlockChange(block.id, 'mediaFile', null);
                                            handleStandaloneLessonBlockChange(block.id, 'mediaFileUrl', undefined);
                                          }}
                                          style={{
                                            padding: '4px 8px',
                                            background: '#e74c3c',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                          }}
                                        >
                                          Удалить
                                        </button>
                                      </div>
                                    )}
                                    {!(block.mediaFile || block.mediaFileUrl) && (
                                      <label htmlFor={`standalone-media-file-${block.id}`} className="add-material__file-label" style={{ cursor: 'pointer' }}>
                                        <svg className="add-material__upload-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                          <path d="M32 20L24 12L16 20M24 12V32M8 36H40" stroke="#7A8699" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <p className="add-material__upload-text">
                                          Загрузите медиа файл
                                        </p>
                                        <button 
                                          type="button" 
                                          className="add-material__file-button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            document.getElementById(`standalone-media-file-${block.id}`)?.click();
                                          }}
                                        >
                                          Выбрать файл
                                        </button>
                                      </label>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="add-material__column">
                              <div className="add-material__field">
                                <label className="add-material__label">
                                  Текст блока (опционально)
                                </label>
                                <textarea
                                  className="add-material__textarea"
                                  placeholder="Введите текст блока"
                                  value={block.text || ''}
                                  onChange={(e) => handleStandaloneLessonBlockChange(block.id, 'text', e.target.value)}
                                  rows={6}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Add Block Button - moved to the end */}
                      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-start' }}>
                        <button
                          type="button"
                          className="add-material__add-block-button"
                          onClick={handleAddStandaloneLessonBlock}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          Добавить блок
                        </button>
                      </div>

                      {/* Показываем поле загрузки материала только если нет блоков или не все блоки типа 'text-media' */}
                      {(standaloneLesson.blocks.length === 0 || !standaloneLesson.blocks.every(block => block.type === 'text-media')) && (
                        <div className="add-material__field" style={{ marginTop: '24px' }}>
                          <label className="add-material__label">
                            Материал для скачивания пользователями (опционально)
                          </label>
                          <div className="add-material__file-upload">
                            <input
                              type="file"
                              id="standalone-download-material"
                              accept=".pdf,.xlsx,.png,.jpeg,.jpg"
                              onChange={handleStandaloneLessonFileSelect}
                              className="add-material__file-input"
                              style={{ display: 'none' }}
                            />
                            {/* Отображение выбранного файла */}
                            {(standaloneLesson.downloadMaterial || standaloneLesson.downloadMaterialUrl) && (
                              <div style={{ 
                                marginBottom: '12px', 
                                padding: '12px', 
                                background: '#f5f5f5', 
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  {standaloneLesson.downloadMaterial instanceof File && standaloneLesson.downloadMaterial.type.startsWith('image/') ? (
                                    <FilePreview file={standaloneLesson.downloadMaterial} />
                                  ) : standaloneLesson.downloadMaterialUrl && (standaloneLesson.downloadMaterialUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                    <img 
                                      src={standaloneLesson.downloadMaterialUrl} 
                                      alt="Preview" 
                                      style={{
                                        width: '60px',
                                        height: '60px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        border: '1px solid #e0e0e0'
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M14 2V8H20" stroke="#2A5E38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  ))}
                                  <div>
                                    <div style={{ fontWeight: '500', color: '#333' }}>
                                      {standaloneLesson.downloadMaterial instanceof File ? standaloneLesson.downloadMaterial.name : (standaloneLesson.downloadMaterialUrl ? standaloneLesson.downloadMaterialUrl.split('/').pop() : 'Файл выбран')}
                                    </div>
                                    {standaloneLesson.downloadMaterial instanceof File && (
                                      <div style={{ fontSize: '12px', color: '#666' }}>
                                        {(standaloneLesson.downloadMaterial.size / 1024 / 1024).toFixed(2)} МБ
                                      </div>
                                    )}
                                    {standaloneLesson.downloadMaterialUrl && !standaloneLesson.downloadMaterial && (
                                      <a 
                                        href={standaloneLesson.downloadMaterialUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ fontSize: '12px', color: '#2A5E38', textDecoration: 'underline' }}
                                      >
                                        Открыть файл
                                      </a>
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleStandaloneLessonChange('downloadMaterial', null);
                                    handleStandaloneLessonChange('downloadMaterialUrl', undefined);
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    background: '#e74c3c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Удалить
                                </button>
                              </div>
                            )}
                            {!(standaloneLesson.downloadMaterial || standaloneLesson.downloadMaterialUrl) && (
                              <label htmlFor="standalone-download-material" className="add-material__file-label" style={{ cursor: 'pointer' }}>
                                <svg className="add-material__upload-icon" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                  <path d="M32 20L24 12L16 20M24 12V32M8 36H40" stroke="#7A8699" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <p className="add-material__upload-text">
                                  Загрузите файл в формате PDF, XLSX, PNG, JPEG до 50 МБ
                                </p>
                                <button 
                                  type="button" 
                                  className="add-material__file-button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    document.getElementById('standalone-download-material')?.click();
                                  }}
                                >
                                  Выбрать файл
                                </button>
                              </label>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : selectedCourseForLesson ? (
                    <button
                      type="button"
                      className="add-material__add-block-button"
                      onClick={() => {
                        const newLesson: Lesson = {
                          id: Date.now().toString(),
                          contentType: 'video',
                          lessonName: '',
                          difficulty: '',
                          duration: '',
                          downloadMaterial: null,
                          blocks: []
                        };
                        setStandaloneLesson(newLesson);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      Начать создание урока
                    </button>
                  ) : null}
                </>
              )}

              {/* Standalone Test Form */}
              {materialType === 'test' && (
                <>
                  <div className="add-material__section-label">Тест</div>
                  
                  {/* Course Selection for Test */}
                  <div className="add-material__field">
                    <label className="add-material__label">
                      Выберите курс<span className="add-material__required">*</span>
                    </label>
                    <Select
                      value={selectedCourseForTest}
                      onChange={(selectedOption) => {
                        setSelectedCourseForTest(selectedOption as { value: number; label: string } | null);
                        setSelectedLessonForTest(null); // Сбрасываем урок при смене курса
                        setStandaloneTest(null); // Сбрасываем тест при смене курса
                      }}
                      options={courses}
                      isLoading={coursesLoading}
                      placeholder="Выберите курс"
                      noOptionsMessage={() => "Нет доступных курсов"}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          border: state.isFocused ? '1px solid #2A5E38' : '1px solid #e0e0e0',
                          borderRadius: '8px',
                          minHeight: '44px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#2A5E38',
                          },
                        }),
                        menu: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          zIndex: 9999,
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? '#2A5E38'
                            : state.isFocused
                            ? '#f0f9f4'
                            : 'white',
                          color: state.isSelected ? 'white' : '#333',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#2A5E38' : '#f0f9f4',
                          },
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#999',
                          fontSize: '14px',
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#7A8699',
                          '&:hover': {
                            color: '#333',
                          },
                        }),
                      }}
                      className="add-material__multi-select"
                    />
                  </div>

                  {/* Lesson Selection for Test */}
                  {selectedCourseForTest && (
                    <div className="add-material__field">
                      <label className="add-material__label">
                        Выберите урок<span className="add-material__required">*</span>
                      </label>
                      <Select
                        value={selectedLessonForTest}
                        onChange={(selectedOption) => {
                          setSelectedLessonForTest(selectedOption as { value: number; label: string } | null);
                          setStandaloneTest(null); // Сбрасываем тест при смене урока
                        }}
                        options={lessonsForTest}
                        isLoading={lessonsLoading}
                        placeholder="Выберите урок для добавления теста"
                        noOptionsMessage={() => "Нет доступных уроков"}
                        menuPortalTarget={document.body}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border: state.isFocused ? '1px solid #2A5E38' : '1px solid #e0e0e0',
                            borderRadius: '8px',
                            minHeight: '44px',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#2A5E38',
                            },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            borderRadius: '8px',
                            zIndex: 9999,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#2A5E38'
                              : state.isFocused
                              ? '#f0f9f4'
                              : 'white',
                            color: state.isSelected ? 'white' : '#333',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: state.isSelected ? '#2A5E38' : '#f0f9f4',
                            },
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#999',
                            fontSize: '14px',
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#7A8699',
                            '&:hover': {
                              color: '#333',
                            },
                          }),
                        }}
                        className="add-material__multi-select"
                      />
                    </div>
                  )}

                  {selectedCourseForTest && selectedLessonForTest && standaloneTest ? (
                    <div className="add-material__test-form">
                      {/* Test Info */}
                      <div className="add-material__test-info">
                        <div className="add-material__test-info-item">
                          <span className="add-material__test-info-label">Количество вопросов</span>
                          <span className="add-material__test-info-value">{standaloneTest.questions.length}</span>
                        </div>
                        <div className="add-material__test-info-item">
                          <span className="add-material__test-info-label">Время для прохождения</span>
                          <div className="add-material__select-wrapper" style={{ minWidth: '200px' }}>
                            <select
                              className="add-material__select"
                              value={standaloneTest.timeToComplete || ''}
                              onChange={(e) => handleStandaloneTestChange('timeToComplete', e.target.value)}
                            >
                              <option value="">Выберите время</option>
                              <option value="1 мин">1 минута</option>
                              <option value="2 мин">2 минуты</option>
                              <option value="3 мин">3 минуты</option>
                              <option value="5 мин">5 минут</option>
                              <option value="10 мин">10 минут</option>
                              <option value="15 мин">15 минут</option>
                            </select>
                            <svg
                              className="add-material__select-arrow"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M4 6L8 10L12 6"
                                stroke="#7A8699"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Questions */}
                      {standaloneTest.questions.map((question, questionIndex) => (
                        <div key={question.id} className="add-material__question-form">
                          <div className="add-material__question-header">
                            <h5 className="add-material__question-title">Вопрос {questionIndex + 1}</h5>
                            <button
                              type="button"
                              className="add-material__remove-button"
                              onClick={() => handleRemoveStandaloneTestQuestion(question.id)}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>

                          {/* Question Type Selection */}
                          <div className="add-material__field-group">
                            <label className="add-material__label">
                              Выберите тип вопроса<span className="add-material__required">*</span>
                            </label>
                            <div className="add-material__radio-group">
                              <label className="add-material__radio-label">
                                <input
                                  type="radio"
                                  name={`standalone-question-type-${question.id}`}
                                  value="single"
                                  checked={question.type === 'single'}
                                  onChange={(e) => handleStandaloneTestQuestionChange(question.id, 'type', e.target.value)}
                                />
                                <span>Один правильный ответ</span>
                              </label>
                              <label className="add-material__radio-label">
                                <input
                                  type="radio"
                                  name={`standalone-question-type-${question.id}`}
                                  value="multiple"
                                  checked={question.type === 'multiple'}
                                  onChange={(e) => handleStandaloneTestQuestionChange(question.id, 'type', e.target.value)}
                                />
                                <span>Несколько правильных ответов</span>
                              </label>
                              {/* Вопрос со свободным ответом временно отключён — бэкенд ещё не проработал логику
                              <label className="add-material__radio-label">
                                <input
                                  type="radio"
                                  name={`standalone-question-type-${question.id}`}
                                  value="open"
                                  checked={question.type === 'open'}
                                  onChange={(e) => handleStandaloneTestQuestionChange(question.id, 'type', e.target.value)}
                                />
                                <span>Открытый вопрос</span>
                              </label>
                              */}
                            </div>
                          </div>

                          {/* Question Text */}
                          <div className="add-material__field">
                            <label className="add-material__label">
                              Текст вопроса<span className="add-material__required">*</span>
                            </label>
                            <div className="add-material__input-wrapper">
                              <input
                                type="text"
                                className="add-material__input"
                                placeholder="Введите текст вопроса"
                                value={question.text}
                                onChange={(e) => handleStandaloneTestQuestionChange(question.id, 'text', e.target.value)}
                              />
                              <svg className="add-material__input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z" stroke="#2A5E38" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>

                          {/* Answer Options */}
                          {(question.type === 'single' || question.type === 'multiple') && (
                            <div className="add-material__options-section">
                              {question.options.map((option, optionIndex) => (
                                <div key={option.id} className="add-material__option-row">
                                  <div className="add-material__option-control">
                                    {question.type === 'single' ? (
                                      <input
                                        type="radio"
                                        name={`standalone-correct-answer-${question.id}`}
                                        checked={option.isCorrect}
                                        onChange={() => handleStandaloneTestCorrectAnswerChange(question.id, option.id, true)}
                                        className="add-material__option-radio"
                                      />
                                    ) : (
                                      <input
                                        type="checkbox"
                                        checked={option.isCorrect}
                                        onChange={(e) => handleStandaloneTestCorrectAnswerChange(question.id, option.id, e.target.checked)}
                                        className="add-material__option-checkbox"
                                      />
                                    )}
                                  </div>
                                  <div className="add-material__option-input-wrapper">
                                    <input
                                      type="text"
                                      className="add-material__option-input"
                                      placeholder="Введите вариант ответа"
                                      value={option.text}
                                      onChange={(e) => handleStandaloneTestOptionChange(question.id, option.id, 'text', e.target.value)}
                                    />
                                    <svg className="add-material__input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z" stroke="#2A5E38" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                  <button
                                    type="button"
                                    className="add-material__remove-option-button"
                                    onClick={() => handleRemoveStandaloneTestOption(question.id, option.id)}
                                    title="Удалить вариант ответа"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <path d="M4 4L12 12M12 4L4 12" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="add-material__add-option-button"
                                onClick={() => handleAddStandaloneTestAnswerOption(question.id)}
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Add Question Button */}
                      <button
                        type="button"
                        className="add-material__add-question-button"
                        onClick={handleAddStandaloneTestQuestion}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        Добавить вопрос
                      </button>
                    </div>
                  ) : selectedCourseForTest && selectedLessonForTest ? (
                    <button
                      type="button"
                      className="add-material__add-block-button"
                      onClick={() => {
                        const newTest: Test = {
                          id: Date.now().toString(),
                          questions: [],
                          timeToComplete: '10 мин'
                        };
                        setStandaloneTest(newTest);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      Начать создание теста
                    </button>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <>
              {/* Knowledge Base Material Type Selection */}
              <div className="add-material__field-group">
                <label className="add-material__label">
                  Выберите тип материала<span className="add-material__required">*</span>
                </label>
                <div className="add-material__radio-group">
                  <label className="add-material__radio-label">
                    <input
                      type="radio"
                      name="knowledgeMaterialType"
                      value="standard"
                      checked={knowledgeMaterialType === 'standard'}
                      onChange={(e) => setKnowledgeMaterialType(e.target.value as 'standard' | 'regulations' | 'lesson')}
                    />
                    <span>Стандарт</span>
                  </label>
                  <label className="add-material__radio-label">
                    <input
                      type="radio"
                      name="knowledgeMaterialType"
                      value="regulations"
                      checked={knowledgeMaterialType === 'regulations'}
                      onChange={(e) => setKnowledgeMaterialType(e.target.value as 'standard' | 'regulations' | 'lesson')}
                    />
                    <span>Нормативка</span>
                  </label>
                  <label className="add-material__radio-label">
                    <input
                      type="radio"
                      name="knowledgeMaterialType"
                      value="lesson"
                      checked={knowledgeMaterialType === 'lesson'}
                      onChange={(e) => setKnowledgeMaterialType(e.target.value as 'standard' | 'regulations' | 'lesson')}
                    />
                    <span>Извлеченный урок</span>
                  </label>
                </div>
              </div>

              {/* Основная информация Section */}
              <div className="add-material__section-label">Основная информация</div>
              <div className="add-material__course-section">
                {/* Left Column */}
                <div className="add-material__column">
                  <div className="add-material__field">
                    <label className="add-material__label">
                      Название стандарта<span className="add-material__required">*</span>
                    </label>
                    <div className="add-material__input-wrapper">
                      <input
                        type="text"
                        className="add-material__input"
                        placeholder="Введите название стандарта"
                        value={standardName}
                        onChange={(e) => setStandardName(e.target.value)}
                      />
                      <svg
                        className="add-material__input-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                          stroke="#2A5E38"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">
                      Раздел<span className="add-material__required">*</span>
                    </label>
                    <div className="add-material__select-wrapper">
                      <select
                        className="add-material__select"
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                      >
                        <option value="">Выберите раздел</option>
                        <option value="section1">Раздел 1</option>
                        <option value="section2">Раздел 2</option>
                        <option value="section3">Раздел 3</option>
                      </select>
                      <svg
                        className="add-material__select-arrow"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="#7A8699"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">Обложка материала</label>
                    <div className="add-material__file-upload">
                      <input
                        type="file"
                        id="cover-upload-knowledge"
                        accept="image/png,image/jpeg"
                        onChange={handleFileSelect}
                        className="add-material__file-input"
                      />
                      <label htmlFor="cover-upload-knowledge" className="add-material__file-label">
                        <svg
                          className="add-material__upload-icon"
                          width="48"
                          height="48"
                          viewBox="0 0 48 48"
                          fill="none"
                        >
                          <path
                            d="M32 20L24 12L16 20M24 12V32M8 36H40"
                            stroke="#7A8699"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="add-material__upload-text">
                          Загрузите файл в формате PNG, JPEG до 5 МБ
                        </p>
                        <button type="button" className="add-material__file-button">
                          Выбрать файл
                        </button>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="add-material__column">
                  <div className="add-material__field">
                    <label className="add-material__label">
                      Классификация<span className="add-material__required">*</span>
                    </label>
                    <div className="add-material__select-wrapper">
                      <select
                        className="add-material__select"
                        value={classification}
                        onChange={(e) => setClassification(e.target.value)}
                      >
                        <option value="">Выберите классификацию</option>
                        <option value="class1">Классификация 1</option>
                        <option value="class2">Классификация 2</option>
                        <option value="class3">Классификация 3</option>
                      </select>
                      <svg
                        className="add-material__select-arrow"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="#7A8699"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">
                      Этап проектирования<span className="add-material__required">*</span>
                    </label>
                    <div className="add-material__select-wrapper">
                      <select
                        className="add-material__select"
                        value={designStage}
                        onChange={(e) => setDesignStage(e.target.value)}
                      >
                        <option value="">Выберите этап проектирования</option>
                        <option value="stage1">Этап 1</option>
                        <option value="stage2">Этап 2</option>
                        <option value="stage3">Этап 3</option>
                      </select>
                      <svg
                        className="add-material__select-arrow"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="#7A8699"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  <div className="add-material__field">
                    <label className="add-material__label">
                      Версия<span className="add-material__required">*</span>
                    </label>
                    <div className="add-material__input-wrapper">
                      <input
                        type="text"
                        className="add-material__input"
                        placeholder="Введите версионность"
                        value={version}
                        onChange={(e) => setVersion(e.target.value)}
                      />
                      <svg
                        className="add-material__input-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
                          stroke="#2A5E38"
                          strokeWidth="1.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Загрузка файла Section */}
              <div className="add-material__section-label">Загрузка файла</div>
              <div className="add-material__field">
                <label className="add-material__label">Материал для скачивания пользователями</label>
                <div className="add-material__file-upload">
                  <input
                    type="file"
                    id="material-upload"
                    accept=".pdf,.xlsx,.png,.jpeg,.jpg"
                    onChange={handleMaterialFileSelect}
                    className="add-material__file-input"
                  />
                  <label htmlFor="material-upload" className="add-material__file-label">
                    <svg
                      className="add-material__upload-icon"
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      fill="none"
                    >
                      <path
                        d="M32 20L24 12L16 20M24 12V32M8 36H40"
                        stroke="#7A8699"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <p className="add-material__upload-text">
                      Загрузите файл в формате PDF, XLSX, PNG, JPEG до 50 МБ
                    </p>
                    <button type="button" className="add-material__file-button">
                      Выбрать файл
                    </button>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="add-material__actions">
        <button
          type="button"
          className="add-material__preview-button"
          onClick={handlePreview}
        >
          Предпросмотр
        </button>
        <button
          type="button"
          className="add-material__publish-button"
          onClick={handlePublish}
          disabled={isPublishing}
        >
          {isPublishing ? 'Публикация...' : 'Опубликовать'}
        </button>
      </div>
    </div>
  );
};

export default AddMaterial;

